import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { confirmDialog } from "primereact/confirmdialog";
import { Skeleton } from "primereact/skeleton";
import { useNavigate } from "react-router-dom";
import useToast from "../../../../components/common/useToast";
import apiFunctions from "../../../../services/api-service";
import { AxiosError } from "axios";
import { errorHandler } from "../../../../utils/HelperFunctions";
import {
    ArrowLeftIcon,
    ArrowUpIcon,
    ArrowDownIcon,
    PlusIcon,
    TrashIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    PhotoIcon,
    ArrowUpTrayIcon,
    EyeIcon,
    VideoCameraIcon,
    Squares2X2Icon,
    StarIcon,
    StopIcon,
    CursorArrowRaysIcon,
    InformationCircleIcon,
    ComputerDesktopIcon,
    DevicePhoneMobileIcon,
    DeviceTabletIcon,
    TvIcon,
    CalendarDaysIcon,
    ListBulletIcon,
    EyeSlashIcon,
    TableCellsIcon,
} from "@heroicons/react/24/outline";
import {
    backgrounds,
    sectionHeight,
    SplitHeading,
    PageVideosSection,
    EventsSection,
    EventCard,
    VideoEmbed,
    SectionBgImage,
    SiteHeaderBar,
    SiteFooterBar,
    ICON_MAP,
    SHADOW_OPTIONS,
    SHAPE_OPTIONS,
    getShadowStyle,
    shapeFillStyle,
    resolveSectionBg,
} from "../../../../components/homeBuilder/BuilderSections";
import type { ScrollAnimation } from "../../../../components/homeBuilder/BuilderSections";
import IconPicker from "../../../../components/homeBuilder/IconPicker";
import { getSectionLabel } from "../../../../constants/sectionOptions";
import HomeContentView from "../../../../components/homeBuilder/HomeContentView";
import logosmall from "../../../../assets/images/logosmall.png";

type ElementKind = "text" | "image" | "icon" | "box" | "button" | "video";
type ShapeType = "rectangle" | "circle" | "triangle" | "pentagon" | "hexagon" | "star" | "line";

type BuilderEl = {
    id: string;
    kind?: ElementKind; // undefined = text (legacy)
    x: number; // % from left
    y: number; // % from top
    link?: string; // /local-route or https://external
    // Header-only: linked text/button marked as a menu item collapses into
    // the hamburger menu on mobile/tablet instead of rendering inline
    menuItem?: boolean;
    // Page Videos / Events sections: marks which element seeded from the
    // section's old fixed title/subtitle text (see migrateSections) — lets
    // the admin drag/style/delete it exactly like any other text element,
    // rather than a separate fixed input field.
    role?: "title" | "subtitle";
    shadow?: "none" | "sm" | "md" | "lg";
    // text / button
    text?: string;
    fontSize?: number;
    color?: string;
    bold?: boolean;
    italic?: boolean;
    // image / box
    url?: string; // image src, or the box's fill image when kind is "box"
    // box: always % of section width. image: % of section width (default,
    // matches all pre-existing data) or px, per widthUnit. Undefined on an
    // image = natural/intrinsic size (no override at all).
    width?: number;
    widthUnit?: "px" | "%"; // image only — which unit `width` is in when set; undefined/box always means %
    height?: number; // box: always px. image: px, or % of the image's own width (see heightUnit)
    heightUnit?: "px" | "%"; // image only — which unit `height` is in; undefined/box always means px
    bg?: string; // box / button background (used when box has no fill image)
    radius?: number; // px (box / button, rectangle shape only)
    // box / shape only
    shapeType?: ShapeType;
    borderColor?: string;
    borderWidth?: number; // px
    rotation?: number; // degrees — box/shape only, mainly for angling a Line
    // icon (kind "icon", or a button's prefix/postfix icon)
    icon?: string;
    size?: number; // px
    iconPos?: "left" | "right"; // button icon before/after the text
};

type Slide = {
    id: string;
    bgIndex: number;
    bgColor?: string; // when set, solid color is used instead of the background image
    bgUrl?: string; // admin-uploaded background image
    bgFixed?: boolean; // background stays fixed while page scrolls
    elements: BuilderEl[];
};

type Section = {
    id: string;
    type: "slider" | "content" | "livevideo" | "pagevideo" | "event";
    bg: string;
    bgUrl?: string; // content sections can have an image background too
    bgFixed?: boolean;
    minHeight: number;
    heightMode?: "custom" | "half" | "full" | "auto"; // "auto" (Free Style, content type only): grows to fit content, no clip/scroll
    title?: string; // livevideo/pagevideo/event heading
    subtitle?: string; // pagevideo/event sub-heading
    displaymode?: "grid" | "slider"; // pagevideo/event: layout on the live site
    slidecols?: number; // pagevideo/event, slider mode only: cards visible per slide (1-4)
    excludedids?: string[]; // pagevideo/event: hidden from THIS section only (source data untouched)
    cardbg?: string; // pagevideo/event: individual card background
    showcardtitle?: boolean; // pagevideo/event: whether the card's title (auto label, or event name) shows
    cardtitlecolor?: string; // pagevideo/event: color of that card title
    // Optional full-width lead image shown above the section's own content,
    // toggled independently of bgUrl (which paints BEHIND content, not as a
    // block in the flow) — animates in on scroll on the live site, see
    // BuilderSections.tsx.
    coverImageUrl?: string;
    coverImageEnabled?: boolean;
    // Defaults to "slide" (fade + slide up) when unset — matches this
    // feature's original, always-animated behavior before the type became
    // choosable.
    coverImageAnimation?: ScrollAnimation;
    // Whole section (background + content together) fades/slides/zooms in on
    // scroll — defaults to "none" (off) since this is a separate, opt-in
    // feature, unlike the cover image above.
    sectionAnimation?: ScrollAnimation;
    // Independent of sectionAnimation — which edge the content enters FROM
    // when it has a cover image (Scroll Direction control, only shown then).
    // Kept as its own field, not folded into sectionAnimation, so picking one
    // never resets/overwrites the other's stored value or displayed dropdown.
    contentDirection?: ScrollAnimation;
    // Seconds — how long the (non-scroll-linked) entrance transition takes.
    // Defaults to 0.7 when unset. Only applies when there's no cover image;
    // WITH a cover image, section.sectionAnimation is tied directly to
    // scroll position instead of a fixed-duration transition (see
    // useScrollProgress in BuilderSections.tsx), so this doesn't apply there.
    animationDuration?: number;
    slides: Slide[];
    elements: BuilderEl[];
};

const newId = () => Math.random().toString(36).slice(2, 10);

// A plain `value={parseInt(text) || min}` controlled input snaps back to `min` the
// instant the field is cleared (parseInt("") is NaN, which is falsy) — Backspace
// never gets to leave the field empty. This keeps its own draft text while focused
// and only clamps/commits on blur, so typing (including clearing the field) behaves
// like a normal input.
const NumberInput: React.FC<{
    value: number;
    min: number;
    max: number;
    onChange: (v: number) => void;
    width?: number;
    step?: number;
    title?: string;
    extraStyle?: React.CSSProperties;
    onFocusExtra?: () => void;
    placeholder?: string;
    // When value === emptyValue, the field displays blank (so `placeholder`
    // can actually show through) instead of a literal "0" — used for
    // "unset/auto" fields like image Height, where 0 doesn't mean "0px" but
    // "no override, follow the natural size".
    emptyValue?: number;
}> = ({ value, min, max, onChange, width = 64, step = 1, title, extraStyle, onFocusExtra, placeholder, emptyValue }) => {
    const [text, setText] = useState(value === emptyValue ? "" : String(value));
    const focusedRef = useRef(false);

    useEffect(() => {
        if (!focusedRef.current) setText(value === emptyValue ? "" : String(value));
    }, [value, emptyValue]);

    return (
        <input
            type="number"
            min={min}
            max={max}
            step={step}
            title={title}
            placeholder={placeholder}
            value={text}
            onFocus={() => {
                focusedRef.current = true;
                onFocusExtra?.();
            }}
            onChange={(e) => {
                setText(e.target.value);
                const parsed = parseInt(e.target.value, 10);
                if (!Number.isNaN(parsed)) onChange(parsed);
            }}
            onBlur={(e) => {
                focusedRef.current = false;
                const parsed = parseInt(e.target.value, 10);
                const clamped = Math.min(max, Math.max(min, Number.isNaN(parsed) ? min : parsed));
                setText(String(clamped));
                onChange(clamped);
            }}
            style={{ width: `${width}px`, borderRadius: "4px", border: "none", padding: "4px 6px", ...extraStyle }}
        />
    );
};

// Unset height (the default) follows the image's own aspect ratio. An
// explicit px height crops via object-fit instead of stretching/distorting
// it. An explicit % height means "% of the image's own rendered width" —
// aspect-ratio expresses that natively regardless of how width itself was
// determined (section %, or a constant-px group), no JS pixel math needed.
const imageHeightStyle = (el: BuilderEl): React.CSSProperties => {
    if (!el.height) return { height: "auto" };
    if (el.heightUnit === "%") {
        return { aspectRatio: `100 / ${el.height}`, height: "auto", objectFit: "cover" };
    }
    return { height: `${el.height}px`, objectFit: "cover" };
};

// Unset width (the default) is the image's natural/intrinsic size — no
// override at all. Set + px is a literal pixel width. Set + % (the default
// once a width IS set, matching every pre-existing stored value) is a
// percentage of the section — or, for a grouped (staticFlow) image, resolved
// against the fixed 1440px design canvas as a real px value instead, same as
// the live site/Preview's constantSize (see the comment at its call site).
const imageWidthStyle = (el: BuilderEl, staticFlow?: boolean): string => {
    if (!el.width) return "auto";
    if (el.widthUnit === "px") return `${el.width}px`;
    return staticFlow ? `${(el.width / 100) * 1440}px` : `${el.width}%`;
};

// Same unset/px/% shape as imageWidthStyle/imageHeightStyle, for element
// kinds (text, button) that don't need the image-only aspect-ratio/objectFit
// behavior — unset stays their natural content-driven size, px/% impose a
// literal box (with overflow:hidden on height so oversized content clips
// instead of spilling past the box, matching how image/box already crop).
const boxHeightStyle = (el: BuilderEl): React.CSSProperties => {
    if (!el.height) return { height: "auto" };
    return { height: `${el.height}px`, overflow: "hidden" };
};

const newTextElement = (light: boolean): BuilderEl => ({
    id: newId(),
    kind: "text",
    text: "New Text",
    x: 40,
    y: 40,
    fontSize: 24,
    color: light ? "#ffffff" : "#111827",
    bold: false,
});

const newIconElement = (light: boolean): BuilderEl => ({
    id: newId(),
    kind: "icon",
    icon: "star",
    x: 45,
    y: 35,
    size: 48,
    color: light ? "#ffffff" : "#111827",
});

const newImageElement = (url: string): BuilderEl => ({
    id: newId(),
    kind: "image",
    url,
    x: 30,
    y: 20,
    width: 30,
});

const newBoxElement = (light: boolean): BuilderEl => ({
    id: newId(),
    kind: "box",
    shapeType: "rectangle",
    x: 35,
    y: 25,
    width: 26,
    height: 130,
    bg: light ? "rgba(255,255,255,0.9)" : "#ffffff",
    radius: 10,
    borderWidth: 0,
});

const newButtonElement = (): BuilderEl => ({
    id: newId(),
    kind: "button",
    text: "Click Me",
    x: 42,
    y: 60,
    fontSize: 16,
    bg: "#22c55e",
    color: "#ffffff",
    radius: 8,
    link: "",
});

const newSlide = (): Slide => ({
    id: newId(),
    bgIndex: 0,
    elements: [
        { id: newId(), kind: "text", text: "Slide Title", x: 10, y: 32, fontSize: 42, color: "#ffffff", bold: true },
        { id: newId(), kind: "text", text: "Slide description goes here.", x: 10, y: 55, fontSize: 18, color: "#ffffff", bold: false },
    ],
});

const newSliderSection = (): Section => ({
    id: newId(),
    type: "slider",
    bg: "#111827",
    minHeight: 420,
    heightMode: "custom",
    slides: [newSlide()],
    elements: [],
});

const newContentSection = (): Section => ({
    id: newId(),
    type: "content",
    bg: "#f3f4f6",
    minHeight: 300,
    // Free Style by default: grows to fit whatever's actually put in it
    // instead of clipping/leaving dead space at a fixed 300px — admin can
    // still switch to Manual (Half/Full/custom px) from the Size toolbar.
    heightMode: "auto",
    slides: [],
    elements: [newTextElement(false)],
});

// "About" is just a Text Section pre-filled with title/description text elements —
// added, dragged, and styled the exact same way as any other section, nothing special.
const newAboutSection = (): Section => ({
    id: newId(),
    type: "content",
    bg: "#ffffff",
    minHeight: 320,
    heightMode: "custom",
    slides: [],
    elements: [
        { id: newId(), kind: "text", text: "WHO WE ARE", x: 10, y: 12, fontSize: 36, color: "#111827", bold: true },
        {
            id: newId(),
            kind: "text",
            text: "Tell your visitors who you are and what you do here.",
            x: 10,
            y: 32,
            fontSize: 16,
            color: "#4b5563",
            bold: false,
        },
    ],
});

// Default title/subtitle as real, draggable/resizable/stylable text elements —
// same as any other section's text — instead of fixed toolbar input fields.
const defaultTitleSubtitleEls = (titleText: string, subtitleText: string): BuilderEl[] => [
    { id: newId(), kind: "text", role: "title", text: titleText, x: 30, y: 15, fontSize: 32, color: "#264164", bold: true },
    { id: newId(), kind: "text", role: "subtitle", text: subtitleText, x: 20, y: 55, fontSize: 16, color: "#6b7280" },
];

const newVideoSection = (type: "livevideo" | "pagevideo"): Section => ({
    id: newId(),
    type,
    // Live Videos stays dark (its own all-dark video-box design); Page Videos'
    // grid below renders on white, so its heading zone defaults to white too —
    // admin can repick via the Bg Color control either way.
    bg: type === "livevideo" ? "#111827" : "#ffffff",
    minHeight: 300,
    heightMode: "custom",
    slides: [],
    // Live Videos gets one draggable/resizable "video" slot marking where the real
    // player renders; Page Videos gets draggable title/subtitle text (the grid itself
    // isn't positionable).
    elements: type === "livevideo"
        ? [{ id: newId(), kind: "video", x: 15, y: 20, width: 70, height: 260 }]
        : defaultTitleSubtitleEls("ABOUT APP", "Watch how each section of the app works."),
});

// Upcoming Events — a grid listing like Page Videos, with draggable title/subtitle text.
const newEventSection = (): Section => ({
    id: newId(),
    type: "event",
    // Grid below renders on white — heading zone matches by default (see
    // newVideoSection's identical note); Bg Color control can repick it.
    bg: "#ffffff",
    minHeight: 300,
    heightMode: "custom",
    slides: [],
    elements: defaultTitleSubtitleEls("UPCOMING EVENTS", "Don't miss what's coming up in the community."),
});

const HEADER_SECTION_ID = "__header__";
const FOOTER_SECTION_ID = "__footer__";

// Shared by both the cover image and whole-section animation dropdowns —
// see ScrollAnimation/scrollRevealStyle in BuilderSections.tsx for what each
// one actually renders as.
const ANIMATION_OPTIONS: { value: ScrollAnimation; label: string }[] = [
    { value: "none", label: "No Animation" },
    { value: "fade", label: "Fade In" },
    { value: "slide", label: "Slide Up" },
    { value: "slide-down", label: "Slide Down" },
    { value: "slide-left", label: "Slide In (from Right)" },
    { value: "slide-right", label: "Slide In (from Left)" },
    { value: "zoom", label: "Zoom In" },
    { value: "flip", label: "Flip" },
];

// Pre-filled with the same logo+title look as the old static default header, but as
// real elements — clickable, draggable, resizable, editable, just like anything else.
const newHeaderSection = (): Section => ({
    id: HEADER_SECTION_ID,
    type: "content",
    bg: "#ffffff",
    minHeight: 90,
    heightMode: "custom",
    slides: [],
    elements: [
        { id: newId(), kind: "image", url: logosmall, x: 2, y: 20, width: 3 },
        { id: newId(), kind: "text", text: "Sagar Samaj", x: 7, y: 32, fontSize: 20, color: "#111827", bold: true },
    ],
});

// Pre-filled with the same copyright + Privacy/Terms links as the old static default footer.
const newFooterSection = (): Section => ({
    id: FOOTER_SECTION_ID,
    type: "content",
    bg: "#000000",
    minHeight: 220,
    heightMode: "custom",
    slides: [],
    elements: [
        { id: newId(), kind: "text", text: "Privacy", x: 8, y: 45, fontSize: 14, color: "#a78bfa", link: "/privacypolicy" },
        { id: newId(), kind: "text", text: "Terms", x: 14, y: 45, fontSize: 14, color: "#a78bfa", link: "/termsandcondition" },
        {
            id: newId(),
            kind: "text",
            text: `Copyright © ${new Date().getFullYear()} RMD Import Export Company. All rights reserved.`,
            x: 45,
            y: 45,
            fontSize: 14,
            color: "#9ca3af",
        },
    ],
});

// Converts old simple slides (title/description) into a slider section
const sectionFromLegacySlides = (slides: any[]): Section => ({
    id: newId(),
    type: "slider",
    bg: "#111827",
    minHeight: 420,
    heightMode: "custom",
    slides: slides.map((s: any, i: number) => ({
        id: newId(),
        bgIndex: i % backgrounds.length,
        elements: [
            { id: newId(), kind: "text" as ElementKind, text: s.title || "", x: 10, y: 32, fontSize: 42, color: "#ffffff", bold: true },
            { id: newId(), kind: "text" as ElementKind, text: s.description || "", x: 10, y: 55, fontSize: 18, color: "#ffffff", bold: false },
        ].filter((el) => el.text),
    })),
    elements: [],
});

// Sections saved before the "video" element existed have no positioned video slot —
// give each such Live Videos section one so it can be dragged/resized like any other.
// Brings sections saved before a given feature existed up to date:
// - livevideo always needs its one positionable "video" slot.
// - pagevideo/event used to carry title/subtitle as plain strings (sec.title /
//   sec.subtitle) rendered via a fixed, non-draggable heading — now they're
//   real text elements the admin can drag/style/delete. Seed them ONCE from
//   whatever text was already saved (or the section's own placeholder) so
//   nothing existing silently disappears; runs on every load but is a no-op
//   once elements already contain a title/subtitle (checked via `role`).
const migrateSections = (secs: Section[]): Section[] =>
    secs.map((s) => {
        if (s.type === "livevideo" && !(s.elements || []).some((el) => el.kind === "video")) {
            return { ...s, elements: [...(s.elements || []), { id: newId(), kind: "video" as ElementKind, x: 15, y: 20, width: 70, height: 260 }] };
        }
        if (s.type === "pagevideo" || s.type === "event") {
            const els = s.elements || [];
            const hasTitle = els.some((el) => el.role === "title");
            const hasSubtitle = els.some((el) => el.role === "subtitle");
            if (hasTitle && hasSubtitle) return s;
            const defaults = defaultTitleSubtitleEls(
                s.title || (s.type === "event" ? "UPCOMING EVENTS" : "ABOUT APP"),
                s.subtitle || (s.type === "event" ? "Don't miss what's coming up in the community." : "Watch how each section of the app works.")
            );
            const added = defaults.filter((d) => (d.role === "title" && !hasTitle) || (d.role === "subtitle" && !hasSubtitle));
            return { ...s, elements: [...added, ...els] };
        }
        return s;
    });

type Selection = { sectionId: string; slideId: string | null; elementId: string } | null;
type UploadPurpose = "slidebg" | "sectionbg" | "imageel" | "shapefill" | "pagebg" | "coverimage";
type BgPickerTarget = { sectionId: string; slideId: string | null; allowTemplates: boolean } | null;

// Only URLs that live under our own uploads folder are ever candidates for deletion —
// external links, the site logo, etc. must never be touched.
const OWNED_IMAGE_PATTERN = /\/public\/homecontent\/([^/?#]+)$/;

const isOwnedImageUrl = (url?: string): url is string => !!url && OWNED_IMAGE_PATTERN.test(url);

const ownedImageFileName = (url: string): string | null => {
    const match = url.match(OWNED_IMAGE_PATTERN);
    return match ? match[1] : null;
};

// Walks every slide/section/element for bgUrl + image/box fill urls, across the page
// sections plus the header/footer canvases, so save-time cleanup can tell which
// uploaded files are still referenced and which just became orphaned.
const collectImageUrls = (sections: Section[], headerSec: Section, footerSec: Section): Set<string> => {
    const urls = new Set<string>();
    const visitElements = (elements: BuilderEl[]) => {
        elements.forEach((el) => {
            if ((el.kind === "image" || el.kind === "box") && isOwnedImageUrl(el.url)) urls.add(el.url as string);
        });
    };
    const visitSection = (sec: Section) => {
        if (isOwnedImageUrl(sec.bgUrl)) urls.add(sec.bgUrl as string);
        sec.slides.forEach((sl) => {
            if (isOwnedImageUrl(sl.bgUrl)) urls.add(sl.bgUrl as string);
            visitElements(sl.elements);
        });
        visitElements(sec.elements);
    };
    sections.forEach(visitSection);
    visitSection(headerSec);
    visitSection(footerSec);
    return urls;
};

// Editor-only alignment aid (never rendered on the live site): a horizontal
// ruler along the top, a vertical ruler along the left, and a full 10%-step
// grid over the canvas — center lines (50%, both axes) drawn stronger so
// it's obvious at a glance whether a dragged element (title/subtitle text,
// image, etc.) actually sits centered instead of just looking close.
// pointer-events:none throughout so it never blocks dragging/clicking the
// real elements underneath.
const RULER_STOPS = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
const AlignmentGrid: React.FC = () => (
    <div className="absolute inset-0" style={{ pointerEvents: "none", zIndex: 40 }}>
        {RULER_STOPS.map((p) => (
            <React.Fragment key={`v-${p}`}>
                <div
                    className="absolute top-0 bottom-0"
                    style={
                        p === 50
                            ? { left: "50%", width: "1px", marginLeft: "-0.5px", background: "rgba(220,38,38,0.75)" }
                            : { left: `${p}%`, width: 0, borderLeft: "1px dashed rgba(100,116,139,0.35)" }
                    }
                />
                <span
                    className="absolute"
                    style={{
                        left: `${p}%`,
                        top: 0,
                        transform: "translateX(-50%)",
                        fontSize: "9px",
                        lineHeight: 1,
                        fontFamily: "monospace",
                        color: p === 50 ? "#dc2626" : "#64748b",
                        fontWeight: p === 50 ? 700 : 400,
                        background: "rgba(255,255,255,0.85)",
                        padding: "1px 2px",
                        borderRadius: "2px",
                    }}
                >
                    {p}
                </span>
            </React.Fragment>
        ))}
        {RULER_STOPS.map((p) => (
            <React.Fragment key={`h-${p}`}>
                <div
                    className="absolute left-0 right-0"
                    style={
                        p === 50
                            ? { top: "50%", height: "1px", marginTop: "-0.5px", background: "rgba(220,38,38,0.75)" }
                            : { top: `${p}%`, height: 0, borderTop: "1px dashed rgba(100,116,139,0.35)" }
                    }
                />
                <span
                    className="absolute"
                    style={{
                        left: 0,
                        top: `${p}%`,
                        transform: "translateY(-50%)",
                        fontSize: "9px",
                        lineHeight: 1,
                        fontFamily: "monospace",
                        color: p === 50 ? "#dc2626" : "#64748b",
                        fontWeight: p === 50 ? 700 : 400,
                        background: "rgba(255,255,255,0.85)",
                        padding: "1px 2px",
                        borderRadius: "2px",
                    }}
                >
                    {p}
                </span>
            </React.Fragment>
        ))}
    </div>
);

const HomeContentPage: React.FC = () => {
    const navigate = useNavigate();
    const { showToast }: any = useToast();
    const initialized = useRef(false);
    // Snapshot of uploaded-image URLs as of the last load/save — diffed against the
    // current content at save time so images dropped from the design get deleted too.
    const savedImageUrlsRef = useRef<Set<string>>(new Set());

    const [sections, setSections] = useState<Section[]>([]);
    const [headerSec, setHeaderSec] = useState<Section>(newHeaderSection());
    const [footerSec, setFooterSec] = useState<Section>(newFooterSection());

    // Free-Style ("auto") section heights, measured from actual rendered
    // content rather than a fixed px number — mirrors the closed-form solve
    // BuilderSections.tsx already uses for the live site/Preview: for element i
    // positioned at top:y%, the section needs H such that y%*H + ownHeight_i
    // <= H, i.e. H >= ownHeight_i / (1 - y%). Taking the max over every
    // [data-el-item] gives the right height in one pass. Keyed by section id
    // since this covers every Free-Style section (main + pinned header/footer)
    // in one query rather than one hook instance per section.
    const [autoHeights, setAutoHeights] = useState<Record<string, number>>({});
    useEffect(() => {
        const measure = () => {
            const next: Record<string, number> = {};
            document.querySelectorAll<HTMLElement>('[data-section-id][data-height-mode="auto"]').forEach((container) => {
                const sectionId = container.dataset.sectionId;
                if (!sectionId) return;
                let required = 0;
                container.querySelectorAll<HTMLElement>("[data-el-item]").forEach((child) => {
                    const yPercent = parseFloat(child.dataset.elY || "0") / 100;
                    if (yPercent >= 1) return; // can't solve (would need infinite height)
                    const neededHeight = child.offsetHeight / (1 - yPercent);
                    if (neededHeight > required) required = neededHeight;
                });
                next[sectionId] = required;
            });
            setAutoHeights(next);
        };
        // rAF: measure once this render's layout (fonts/edits/wrap) has settled.
        const raf = requestAnimationFrame(measure);
        // Re-measure once web fonts finish loading — text metrics (and therefore
        // wrap points/heights) can shift slightly once a fallback font swaps
        // for the real one.
        document.fonts?.ready?.then(measure).catch(() => {});
        window.addEventListener("resize", measure);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", measure);
        };
    }, [sections, headerSec, footerSec]);

    const [pageBg, setPageBg] = useState("");
    // Whole-page backdrop image, layered behind pageBg's color and every
    // section — pagebgimagefixed keeps it pinned while sections' own opaque
    // backgrounds scroll up and cover it (see HomeContentView.tsx).
    const [pageBgImage, setPageBgImage] = useState("");
    const [pageBgImageFixed, setPageBgImageFixed] = useState(true);
    // Per-page on/off for the floating app-install/rate-us banner (see
    // AppInstallBanner.tsx) — off by default (its own smart mobile-only
    // behavior applies); turning it on for a page forces it to show on every
    // device, including desktop.
    const [showInstallBanner, setShowInstallBanner] = useState(false);
    // Per-page SEO overrides — only meaningful for sitepages (activePageId
    // set); the Home page's tags come from the base index.html instead, so
    // these stay blank and unsaved whenever activePageId is null.
    const [metaTitle, setMetaTitle] = useState("");
    const [metaDescription, setMetaDescription] = useState("");
    const [metaKeywords, setMetaKeywords] = useState("");
    const [showGrid, setShowGrid] = useState(false);
    const [headerCfg, setHeaderCfg] = useState<any>({});
    const [footerCfg, setFooterCfg] = useState<any>({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [previewMode, setPreviewMode] = useState<"desktop" | "mobile" | "tablet" | "large">("desktop");

    // Multi-page: extra builder pages (About Us, ...) beyond Home. null =
    // editing the Home page (the homecontent document); an id = editing that
    // sitepage document. The same sections/pageBg states are reused — they
    // just hold whichever page is active.
    const [sitePages, setSitePages] = useState<{ _id: string; slug: string; title: string; isactive: boolean }[]>([]);
    const [activePageId, setActivePageId] = useState<string | null>(null);
    const [showAddPage, setShowAddPage] = useState(false);
    // Replaces window.confirm's native browser popup with PrimeReact's themed
    // ConfirmDialog (mounted once in App.tsx) — askConfirm(message, onConfirm)
    // is a drop-in swap for `if (window.confirm(message)) { ... }`.
    const askConfirm = (message: string, onConfirm: () => void, opts?: { onCancel?: () => void; okLabel?: string; cancelLabel?: string }) =>
        confirmDialog({
            message,
            header: "Confirm",
            accept: onConfirm,
            reject: opts?.onCancel,
            acceptLabel: opts?.okLabel || "OK",
            rejectLabel: opts?.cancelLabel || "Cancel",
        });
    const [newPageTitle, setNewPageTitle] = useState("");
    const [newPageSlug, setNewPageSlug] = useState("");
    const [creatingPage, setCreatingPage] = useState(false);

    const slugify = (raw: string) =>
        raw.toLowerCase().trim().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "").replace(/-+/g, "-").replace(/^-|-$/g, "");

    const activePage = activePageId ? sitePages.find((p) => p._id === activePageId) || null : null;

    // Preview can show ANY page, not just the one being edited: the active
    // page previews live from the unsaved in-memory state; other pages are
    // fetched (their last-saved version) and cached for the dialog session.
    const [previewPageId, setPreviewPageId] = useState<string | null>(null);
    const [previewCache, setPreviewCache] = useState<Record<string, { sections: Section[]; pagebg: string; pagebgimage: string; pagebgimagefixed: boolean; showinstallbanner: boolean }>>({});
    const [previewFetching, setPreviewFetching] = useState(false);

    const previewCacheKey = (id: string | null) => id ?? "home";

    const selectPreviewPage = async (pageId: string | null) => {
        setPreviewPageId(pageId);
        if (pageId === activePageId || previewCache[previewCacheKey(pageId)]) return;
        try {
            setPreviewFetching(true);
            if (pageId === null) {
                const res = await apiFunctions.get("/homecontent");
                const data = res?.data?.data;
                setPreviewCache((prev) => ({
                    ...prev,
                    home: {
                        sections: data?.sections?.length ? migrateSections(data.sections) : [],
                        pagebg: data?.pagebg || "",
                        pagebgimage: data?.pagebgimage || "",
                        pagebgimagefixed: data?.pagebgimagefixed !== false,
                        showinstallbanner: !!data?.showinstallbanner,
                    },
                }));
            } else {
                const res = await apiFunctions.get(`/sitepage/${pageId}`);
                const page = res?.data?.data;
                setPreviewCache((prev) => ({
                    ...prev,
                    [pageId]: {
                        sections: migrateSections(page?.sections || []),
                        pagebg: page?.pagebg || "",
                        pagebgimage: page?.pagebgimage || "",
                        pagebgimagefixed: page?.pagebgimagefixed !== false,
                        showinstallbanner: !!page?.showinstallbanner,
                    },
                }));
            }
        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            setPreviewFetching(false);
        }
    };

    // Links clicked INSIDE the preview must navigate the PREVIEW, not the
    // admin app: /p/<slug> and / switch the previewed page in place; other
    // internal routes open in a new tab so the builder is never lost.
    // External links keep their own target=_blank. Works for drawer links
    // too — the drawer is a DOM portal, but React portal events still bubble
    // through the React tree, so this capture handler sees them.
    const handlePreviewLinkClick = (e: React.MouseEvent) => {
        const anchor = (e.target as HTMLElement).closest?.("a");
        if (!anchor) return;
        const href = anchor.getAttribute("href") || "";
        if (!href || href.startsWith("#") || /^https?:\/\//i.test(href)) return;
        e.preventDefault();
        e.stopPropagation();
        if (href === "/") {
            selectPreviewPage(null);
            return;
        }
        const match = href.match(/^\/p\/([a-z0-9-]+)/i);
        if (match) {
            const page = sitePages.find((p) => p.slug === match[1].toLowerCase());
            if (page) {
                selectPreviewPage(page._id);
            } else {
                showToast({ severity: "warn", summary: "Page Not Found", detail: `"/p/${match[1]}" slug nu koi page nathi.`, life: 3000 });
            }
            return;
        }
        window.open(href, "_blank");
    };

    // What the preview dialog actually renders for the selected page
    const previewContent = previewPageId === activePageId
        ? { sections, pagebg: pageBg, pagebgimage: pageBgImage, pagebgimagefixed: pageBgImageFixed, showinstallbanner: showInstallBanner }
        : previewCache[previewCacheKey(previewPageId)] || null;

    const previewPageName = previewPageId === null
        ? "Home"
        : sitePages.find((p) => p._id === previewPageId)?.title || "Page";

    // The builder's drag/drop canvas needs real screen real estate — block phones and
    // tablets entirely rather than offering a broken cramped layout.
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 1024);
    useEffect(() => {
        const handleResize = () => setIsSmallScreen(window.innerWidth < 1024);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // real videos, used so the canvas and preview look exactly like the public homepage
    const [liveVideos, setLiveVideos] = useState<any[]>([]);
    const [pageVideos, setPageVideos] = useState<any[]>([]);
    // Fetched with ?all=1 so the Manage dialog can show/toggle past events too
    // (see loadEventsData below) — everywhere else (canvas preview, Preview
    // dialog) must only ever consider upcomingEventsData: a past event must
    // NEVER appear in the actual Events section, regardless of its
    // visible/hidden toggle — the live site's own fetch (no ?all=1) already
    // excludes it, and the builder's own previews have to agree with that.
    const [eventsData, setEventsData] = useState<any[]>([]);
    const isUpcomingEvent = (ev: any) => {
        if (!ev.event_date) return true;
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        return new Date(ev.event_date) >= startOfToday;
    };
    const upcomingEventsData = eventsData.filter(isUpcomingEvent);
    const [uploadedImages, setUploadedImages] = useState<{ name: string; url: string }[]>([]);

    const [selected, setSelected] = useState<Selection>(null);
    // Ctrl/Cmd+click adds elements here instead of replacing `selected` — once
    // non-empty, this drives the toolbar/outline instead of `selected` (see
    // activeSelection below), so the exact same per-kind toolbar controls
    // apply a change to every selected element at once.
    const [multiSelected, setMultiSelected] = useState<{ sectionId: string; slideId: string | null; elementId: string }[]>([]);
    // Live drag-rectangle while a marquee (click-drag on empty canvas
    // background) selection is in progress — canvas-relative so it stays put
    // regardless of scroll, cleared once the drag ends.
    const [marqueeRect, setMarqueeRect] = useState<{ sectionId: string; slideId: string | null; left: number; top: number; width: number; height: number } | null>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    // Every element the toolbar/outline treats as "selected" — either the
    // single `selected` pointer, or (once anything has been ctrl/cmd-clicked)
    // all of multiSelected. The toolbar reads/writes through this uniformly
    // so the same controls edit one element or many without separate code
    // paths per control; the FIRST entry (selection order) is the "primary"
    // used to position the toolbar and to display each field's current value.
    const activeSelection: { sectionId: string; slideId: string | null; elementId: string }[] =
        multiSelected.length > 0 ? multiSelected : selected ? [selected] : [];
    const [toolbarPos, setToolbarPos] = useState<{ top: number; left: number } | null>(null);
    const [activeSlide, setActiveSlide] = useState<Record<string, number>>({});
    const [uploadingFor, setUploadingFor] = useState<string | null>(null);
    const [bgPicker, setBgPicker] = useState<BgPickerTarget>(null);
    // Which events/videos a "event"/"pagevideo" section shows — hides an item
    // from THIS home section only, without touching the actual event/video
    // (managed separately under Settings → Events / Page Videos).
    const [manageFor, setManageFor] = useState<{ sectionId: string; kind: "event" | "pagevideo" } | null>(null);
    const uploadTargetRef = useRef<{ purpose: UploadPurpose; sectionId: string; slideId?: string; elementId?: string } | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const dragRef = useRef<{
        sectionId: string;
        slideId: string | null;
        elementId: string;
        isText: boolean;
        startX: number;
        startY: number;
        origX: number;
        origY: number;
        rect: DOMRect;
        moved: boolean;
    } | null>(null);

    async function loadUploadedImages() {
        try {
            const res = await apiFunctions.get("/homecontent/images");
            if (res?.data?.success) setUploadedImages(res.data.data || []);
        } catch {
            /* non-fatal */
        }
    }

    async function getHomeContentData() {
        try {
            setLoading(true);
            const response = await apiFunctions.get("/homecontent");
            if (response?.data?.success) {
                const data = response.data.data;
                if (data) {
                    // A saved document already exists — always show exactly what was saved,
                    // even if that means zero sections, so editing continues from the real state.
                    let loadedSections: Section[];
                    if (data.sections?.length) {
                        loadedSections = migrateSections(data.sections);
                    } else if (data.slides?.length) {
                        loadedSections = [sectionFromLegacySlides(data.slides)];
                    } else {
                        loadedSections = [];
                    }
                    setSections(loadedSections);
                    setPageBg(data.pagebg || "");
                    setPageBgImage(data.pagebgimage || "");
                    setPageBgImageFixed(data.pagebgimagefixed !== false);
                    setShowInstallBanner(!!data.showinstallbanner);
                    setHeaderCfg(data.header || {});
                    setFooterCfg(data.footer || {});
                    let loadedHeaderSec = newHeaderSection();
                    if (data.header?.custom?.id) {
                        const loadedHeader = { ...newHeaderSection(), ...data.header.custom, id: HEADER_SECTION_ID };
                        // Sections saved before the header had default elements come back empty —
                        // fall back to the fresh, pre-filled default instead of a permanently blank canvas.
                        loadedHeaderSec = loadedHeader.elements.length > 0 ? loadedHeader : newHeaderSection();
                        setHeaderSec(loadedHeaderSec);
                    }
                    let loadedFooterSec = newFooterSection();
                    if (data.footer?.custom?.id) {
                        const loadedFooter = { ...newFooterSection(), ...data.footer.custom, id: FOOTER_SECTION_ID };
                        loadedFooterSec = loadedFooter.elements.length > 0 ? loadedFooter : newFooterSection();
                        setFooterSec(loadedFooterSec);
                    }
                    savedImageUrlsRef.current = collectImageUrls(loadedSections, loadedHeaderSec, loadedFooterSec);
                } else {
                    // Nothing has ever been saved — seed a starter slider so the page isn't blank
                    setSections([newSliderSection()]);
                }
            } else {
                setSections([newSliderSection()]);
                showToast({
                    severity: "error",
                    summary: "Error",
                    detail: response?.data?.message || "Failed to load home content",
                    life: 3000,
                });
            }
        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            // fresh page state = fresh undo history (undo must never restore
            // another page's content into this one)
            resetHistory();
            setLoading(false);
        }
    }

    useEffect(() => {
        if (!initialized.current) {
            initialized.current = true;
            getHomeContentData();
            loadSitePages();
            loadUploadedImages();
            apiFunctions.get("/livevideo/webapp/list").then((res) => {
                if (res?.data?.success) setLiveVideos(res.data.data || []);
            }).catch(() => { });
            apiFunctions.get("/pagevideo/webapp/list").then((res) => {
                if (res?.data?.success) setPageVideos(res.data.data || []);
            }).catch(() => { });
            apiFunctions.get("/event/webapp/list?all=1").then((res) => {
                if (res?.data?.success) setEventsData(res.data.data || []);
            }).catch(() => { });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleBack = () => {
        navigate('/admin/settings');
    };

    // ---------- multi-page ----------

    async function loadSitePages() {
        try {
            const res = await apiFunctions.get("/sitepage/list");
            if (res?.data?.success) setSitePages(res.data.data || []);
        } catch {
            /* non-fatal */
        }
    }

    // Switching pages reloads the target from the server — unsaved edits on
    // the page being left are discarded (after an explicit confirm).
    const switchToPage = (pageId: string | null) => {
        if (pageId === activePageId) return;
        askConfirm("Switching page will discard any unsaved changes on the current page. Continue?", () => doSwitchToPage(pageId));
    };

    const doSwitchToPage = async (pageId: string | null) => {
        setSelected(null);
        setEditingId(null);

        if (pageId === null) {
            setActivePageId(null);
            setMetaTitle("");
            setMetaDescription("");
            setMetaKeywords("");
            await getHomeContentData();
            return;
        }

        try {
            setLoading(true);
            const res = await apiFunctions.get(`/sitepage/${pageId}`);
            if (res?.data?.success && res.data.data) {
                const page = res.data.data;
                setSections(migrateSections(page.sections || []));
                setPageBg(page.pagebg || "");
                setPageBgImage(page.pagebgimage || "");
                setPageBgImageFixed(page.pagebgimagefixed !== false);
                setShowInstallBanner(!!page.showinstallbanner);
                setMetaTitle(page.metatitle || "");
                setMetaDescription(page.metadescription || "");
                setMetaKeywords(page.metakeywords || "");
                setActivePageId(pageId);
                resetHistory();
            } else {
                showToast({ severity: "error", summary: "Error", detail: res?.data?.message || "Failed to load page", life: 3000 });
            }
        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            setLoading(false);
        }
    };

    const handleCreatePage = async () => {
        if (!newPageTitle.trim()) {
            showToast({ severity: "warn", summary: "Title Required", detail: "Please enter a page title", life: 3000 });
            return;
        }
        try {
            setCreatingPage(true);
            const res = await apiFunctions.post("/sitepage", {
                title: newPageTitle.trim(),
                slug: newPageSlug.trim() || slugify(newPageTitle),
            });
            if (res?.data?.success) {
                const page = res.data.data;
                setSitePages((prev) => [...prev, page]);
                setShowAddPage(false);
                setNewPageTitle("");
                setNewPageSlug("");
                // Open the new (empty) page directly for editing
                setSelected(null);
                setEditingId(null);
                setSections([]);
                setPageBg("");
                setPageBgImage("");
                setPageBgImageFixed(true);
                setShowInstallBanner(false);
                // Backend auto-fills these from the base site keyword list on
                // creation (see util/seoDefaults.js) — never blank, editable
                // from here if the admin wants something more page-specific.
                setMetaTitle(page.metatitle || "");
                setMetaDescription(page.metadescription || "");
                setMetaKeywords(page.metakeywords || "");
                setActivePageId(page._id);
                resetHistory();
                showToast({ severity: "success", summary: "Success", detail: res.data.message, life: 3000 });
            } else {
                showToast({ severity: "error", summary: "Error", detail: res?.data?.message || "Failed to create page", life: 3000 });
            }
        } catch (err: any) {
            const msg = err?.response?.data?.message;
            if (msg) showToast({ severity: "error", summary: "Error", detail: msg, life: 4000 });
            else errorHandler(err as AxiosError);
        } finally {
            setCreatingPage(false);
        }
    };

    const handleDeletePage = () => {
        if (!activePageId || !activePage) return;
        askConfirm(`Delete page "${activePage.title}" permanently? This cannot be undone.`, doDeletePage);
    };

    const doDeletePage = async () => {
        if (!activePageId) return;
        try {
            const res = await apiFunctions.delete(`/sitepage/${activePageId}`);
            if (res?.data?.success) {
                setSitePages((prev) => prev.filter((p) => p._id !== activePageId));
                setActivePageId(null);
                await getHomeContentData();
                showToast({ severity: "success", summary: "Deleted", detail: res.data.message, life: 3000 });
            } else {
                showToast({ severity: "error", summary: "Error", detail: res?.data?.message || "Failed to delete page", life: 3000 });
            }
        } catch (err) {
            errorHandler(err as AxiosError);
        }
    };

    // ---------- generic section mutation (works for page sections + header/footer) ----------

    const mutateSectionById = (sectionId: string, fn: (s: Section) => Section) => {
        if (sectionId === HEADER_SECTION_ID) {
            setHeaderSec((prev) => fn(prev));
            return;
        }
        if (sectionId === FOOTER_SECTION_ID) {
            setFooterSec((prev) => fn(prev));
            return;
        }
        setSections((prev) => prev.map((s) => (s.id === sectionId ? fn(s) : s)));
    };

    const updateSection = (sectionId: string, patch: Partial<Section>) => {
        mutateSectionById(sectionId, (s) => ({ ...s, ...patch }));
    };

    const findSection = (sectionId: string): Section | undefined => {
        if (sectionId === HEADER_SECTION_ID) return headerSec;
        if (sectionId === FOOTER_SECTION_ID) return footerSec;
        return sections.find((s) => s.id === sectionId);
    };

    // ---------- element helpers ----------

    const updateElement = (sectionId: string, slideId: string | null, elementId: string, patch: Partial<BuilderEl>) => {
        mutateSectionById(sectionId, (sec) => {
            if (slideId) {
                return {
                    ...sec,
                    slides: sec.slides.map((sl) =>
                        sl.id === slideId
                            ? { ...sl, elements: sl.elements.map((el) => (el.id === elementId ? { ...el, ...patch } : el)) }
                            : sl
                    ),
                };
            }
            return { ...sec, elements: sec.elements.map((el) => (el.id === elementId ? { ...el, ...patch } : el)) };
        });
    };

    const removeElement = (sectionId: string, slideId: string | null, elementId: string) => {
        mutateSectionById(sectionId, (sec) => {
            if (slideId) {
                return {
                    ...sec,
                    slides: sec.slides.map((sl) =>
                        sl.id === slideId ? { ...sl, elements: sl.elements.filter((el) => el.id !== elementId) } : sl
                    ),
                };
            }
            return { ...sec, elements: sec.elements.filter((el) => el.id !== elementId) };
        });
        setSelected(null);
        setEditingId(null);
    };

    const clearSelection = () => {
        setSelected(null);
        setEditingId(null);
        setMultiSelected([]);
    };

    const removeSelection = () => {
        if (multiSelected.length > 0) {
            multiSelected.forEach((t) => removeElement(t.sectionId, t.slideId, t.elementId));
            setMultiSelected([]);
            return;
        }
        if (selected) removeElement(selected.sectionId, selected.slideId, selected.elementId);
    };

    // ---------- undo/redo + element clipboard ----------

    // The keydown effect below is subscribed per-selection, so its closures go
    // stale for everything else — all keyboard actions read through refs.
    const stateRef = useRef({ sections, headerSec, footerSec });
    stateRef.current = { sections, headerSec, footerSec };

    type HistorySnapshot = { sections: Section[]; headerSec: Section; footerSec: Section; pageBg: string; pageBgImage: string; pageBgImageFixed: boolean };
    const historyRef = useRef<{ past: HistorySnapshot[]; future: HistorySnapshot[]; current: HistorySnapshot | null; skip: boolean }>({
        past: [],
        future: [],
        current: null,
        skip: false,
    });
    const clipboardRef = useRef<{ el: BuilderEl; sectionId: string; slideId: string | null } | null>(null);
    // Frozen copy of a text element's content as of when inline editing began —
    // rendered as the contentEditable's children for the whole edit session so
    // per-keystroke state commits never reset the cursor (see onUp above).
    const editingInitialTextRef = useRef<string>("");

    const resetHistory = () => {
        historyRef.current = { past: [], future: [], current: null, skip: false };
    };

    // Checkpoint the editable state into history, debounced so a drag (dozens
    // of mousemove state updates) collapses into ONE undo step. States are
    // updated immutably everywhere, so snapshots are cheap reference copies.
    useEffect(() => {
        if (loading) return;
        const h = historyRef.current;
        const snapshot: HistorySnapshot = { sections, headerSec, footerSec, pageBg, pageBgImage, pageBgImageFixed };
        if (h.skip) {
            // this change came from undo/redo itself — don't record it again
            h.skip = false;
            h.current = snapshot;
            return;
        }
        const timer = setTimeout(() => {
            if (h.current) {
                h.past.push(h.current);
                if (h.past.length > 50) h.past.shift();
                h.future = [];
            }
            h.current = snapshot;
        }, 350);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sections, headerSec, footerSec, pageBg, pageBgImage, pageBgImageFixed, loading]);

    const applySnapshot = (snap: HistorySnapshot) => {
        historyRef.current.skip = true;
        setSections(snap.sections);
        setHeaderSec(snap.headerSec);
        setFooterSec(snap.footerSec);
        setPageBg(snap.pageBg);
        setPageBgImage(snap.pageBgImage);
        setPageBgImageFixed(snap.pageBgImageFixed);
        setSelected(null);
        setEditingId(null);
    };

    const undo = () => {
        const h = historyRef.current;
        if (!h.past.length) return;
        const prev = h.past.pop()!;
        if (h.current) h.future.push(h.current);
        h.current = prev;
        applySnapshot(prev);
    };

    const redo = () => {
        const h = historyRef.current;
        if (!h.future.length) return;
        const next = h.future.pop()!;
        if (h.current) h.past.push(h.current);
        h.current = next;
        applySnapshot(next);
    };

    const findElementIn = (sectionId: string, slideId: string | null, elementId: string): BuilderEl | null => {
        const { sections: secs, headerSec: hs, footerSec: fs } = stateRef.current;
        const sec = sectionId === HEADER_SECTION_ID ? hs : sectionId === FOOTER_SECTION_ID ? fs : secs.find((s) => s.id === sectionId);
        if (!sec) return null;
        const pool = slideId ? sec.slides.find((sl) => sl.id === slideId)?.elements : sec.elements;
        return pool?.find((el) => el.id === elementId) || null;
    };

    const copySelectedElement = () => {
        if (!selected) return;
        const el = findElementIn(selected.sectionId, selected.slideId, selected.elementId);
        if (!el || el.kind === "video") return; // video slot is structural
        clipboardRef.current = { el: JSON.parse(JSON.stringify(el)), sectionId: selected.sectionId, slideId: selected.slideId };
    };

    const pasteClipboardElement = () => {
        const clip = clipboardRef.current;
        if (!clip) return;
        // paste into the currently selected section (so you can copy from one
        // section and paste into another), else back where it was copied from
        const targetSectionId = selected?.sectionId || clip.sectionId;
        const targetSlideId = selected?.sectionId ? selected.slideId : clip.slideId;
        const newEl: BuilderEl = {
            ...JSON.parse(JSON.stringify(clip.el)),
            id: newId(),
            x: Math.min(95, (clip.el.x || 0) + 3),
            y: Math.min(93, (clip.el.y || 0) + 3),
        };
        mutateSectionById(targetSectionId, (sec) => {
            if (targetSlideId) {
                return { ...sec, slides: sec.slides.map((sl) => (sl.id === targetSlideId ? { ...sl, elements: [...sl.elements, newEl] } : sl)) };
            }
            return { ...sec, elements: [...sec.elements, newEl] };
        });
        setSelected({ sectionId: targetSectionId, slideId: targetSlideId, elementId: newEl.id });
    };

    // Nudge an element by a RELATIVE delta inside the functional state update —
    // the arrow-key handler below is subscribed once per selection, so reading
    // x/y from its (stale) closure and writing absolute values back would keep
    // moving from the original position on every repeat keypress.
    const nudgeElement = (sectionId: string, slideId: string | null, elementId: string, dx: number, dy: number) => {
        const move = (el: BuilderEl): BuilderEl =>
            el.id === elementId
                ? {
                    ...el,
                    x: Math.min(98, Math.max(0, Math.round(((el.x || 0) + dx) * 10) / 10)),
                    y: Math.min(96, Math.max(0, Math.round(((el.y || 0) + dy) * 10) / 10)),
                }
                : el;
        mutateSectionById(sectionId, (sec) => {
            if (slideId) {
                return { ...sec, slides: sec.slides.map((sl) => (sl.id === slideId ? { ...sl, elements: sl.elements.map(move) } : sl)) };
            }
            return { ...sec, elements: sec.elements.map(move) };
        });
    };

    // Builder keyboard shortcuts — but never while typing in a form field or
    // editing inline text (contentEditable), where these keys must behave
    // normally. Ctrl+Z/Y undo/redo, Ctrl+C/X/V copy/cut/paste the element,
    // Ctrl+D duplicates, Delete removes, arrows nudge (Shift = big steps).
    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            const active = document.activeElement as HTMLElement | null;
            const tag = active?.tagName;
            const isFormField = tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || !!active?.isContentEditable;
            if (isFormField) return;

            const meta = e.ctrlKey || e.metaKey;
            if (meta) {
                const k = e.key.toLowerCase();
                if (k === "z") {
                    e.preventDefault();
                    if (e.shiftKey) redo(); else undo();
                    return;
                }
                if (k === "y") {
                    e.preventDefault();
                    redo();
                    return;
                }
                if (k === "c" && selected) {
                    copySelectedElement();
                    return;
                }
                if (k === "x" && selected) {
                    if (getSelectedElement()?.kind === "video") return;
                    e.preventDefault();
                    copySelectedElement();
                    removeElement(selected.sectionId, selected.slideId, selected.elementId);
                    return;
                }
                if (k === "v" && clipboardRef.current) {
                    e.preventDefault();
                    pasteClipboardElement();
                    return;
                }
                if (k === "d" && selected) {
                    e.preventDefault();
                    copySelectedElement();
                    pasteClipboardElement();
                    return;
                }
                return;
            }

            if (!selected && multiSelected.length === 0) return;
            const isArrow = e.key === "ArrowUp" || e.key === "ArrowDown" || e.key === "ArrowLeft" || e.key === "ArrowRight";
            if (e.key !== "Delete" && e.key !== "Backspace" && !isArrow) return;

            if (isArrow) {
                if (!selected) return; // nudging a multi-selection as a group isn't supported yet
                e.preventDefault();
                const step = e.shiftKey ? 2 : 0.5;
                const dx = e.key === "ArrowLeft" ? -step : e.key === "ArrowRight" ? step : 0;
                const dy = e.key === "ArrowUp" ? -step : e.key === "ArrowDown" ? step : 0;
                nudgeElement(selected.sectionId, selected.slideId, selected.elementId, dx, dy);
                return;
            }

            if (multiSelected.length > 0) {
                e.preventDefault();
                removeSelection();
                return;
            }

            if (getSelectedElement()?.kind === "video") return; // structural video slot, not deletable
            e.preventDefault();
            removeElement(selected!.sectionId, selected!.slideId, selected!.elementId);
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected, multiSelected]);

    // Floating toolbar: track the selected element's live position so the toolbar
    // hovers just above it, instead of sitting fixed at the top of the page.
    const toolbarRef = useRef<HTMLDivElement | null>(null);

    // Clicking anywhere OUTSIDE the selected component deselects it (and hides
    // the floating toolbar) — except clicks that are part of working WITH it:
    // the toolbar itself (incl. its in-tree popups like the icon picker), any
    // element (they manage their own selection on mousedown), and PrimeReact
    // dialogs opened from the toolbar (background/image pickers).
    useEffect(() => {
        if (!selected && multiSelected.length === 0) return;
        const onDocMouseDown = (e: MouseEvent) => {
            const t = e.target as HTMLElement | null;
            if (!t) return;
            if (t.closest("[data-element-id]")) return;
            if (toolbarRef.current?.contains(t)) return;
            if (t.closest(".p-dialog, .p-dialog-mask")) return;
            clearSelection();
        };
        document.addEventListener("mousedown", onDocMouseDown);
        return () => document.removeEventListener("mousedown", onDocMouseDown);
    }, [selected, multiSelected]);
    const recalcToolbarPos = useCallback(() => {
        const primary = activeSelection[0];
        if (!primary) {
            setToolbarPos(null);
            return;
        }
        const node = document.querySelector(`[data-element-id="${primary.elementId}"]`) as HTMLElement | null;
        if (!node) {
            setToolbarPos(null);
            return;
        }
        const rect = node.getBoundingClientRect();
        // Measure the toolbar's real height (it wraps to 1-3 rows depending on the
        // selected kind) instead of guessing, so it never overlaps the component.
        const toolbarHeight = toolbarRef.current?.getBoundingClientRect().height || 100;
        const gap = 12;
        const top = Math.max(8, rect.top - toolbarHeight - gap);
        const left = Math.min(Math.max(8, rect.left), window.innerWidth - 420);
        setToolbarPos((prev) => (prev && prev.top === top && prev.left === left ? prev : { top, left }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected, multiSelected]);

    // Recompute on every state change that could move the element (drag/resize update
    // sections/headerSec/footerSec on each mousemove), plus on scroll/resize of the page.
    useLayoutEffect(() => {
        recalcToolbarPos();
    }, [recalcToolbarPos, sections, headerSec, footerSec, activeSlide]);

    useEffect(() => {
        window.addEventListener("scroll", recalcToolbarPos, true);
        window.addEventListener("resize", recalcToolbarPos);
        return () => {
            window.removeEventListener("scroll", recalcToolbarPos, true);
            window.removeEventListener("resize", recalcToolbarPos);
        };
    }, [recalcToolbarPos]);

    const getSelectedElement = (): BuilderEl | null => {
        if (!selected) return null;
        const sec = findSection(selected.sectionId);
        if (!sec) return null;
        const list = selected.slideId ? sec.slides.find((sl) => sl.id === selected.slideId)?.elements : sec.elements;
        return list?.find((el) => el.id === selected.elementId) || null;
    };

    const addElementToSection = (sec: Section, el: BuilderEl) => {
        // boxes go to the back so text/icons stay visible on top of them
        const put = (list: BuilderEl[]) => (el.kind === "box" ? [el, ...list] : [...list, el]);
        if (sec.type === "slider") {
            const idx = activeSlide[sec.id] || 0;
            const slide = sec.slides[idx];
            if (!slide) return;
            mutateSectionById(sec.id, (s) => ({
                ...s,
                slides: s.slides.map((sl, i) => (i === idx ? { ...sl, elements: put(sl.elements) } : sl)),
            }));
            setSelected({ sectionId: sec.id, slideId: slide.id, elementId: el.id });
        } else {
            mutateSectionById(sec.id, (s) => ({ ...s, elements: put(s.elements) }));
            setSelected({ sectionId: sec.id, slideId: null, elementId: el.id });
        }
        setEditingId(null);
    };

    // ---------- drag / click-to-edit ----------

    const onElementMouseDown = (
        e: React.MouseEvent,
        sectionId: string,
        slideId: string | null,
        el: BuilderEl
    ) => {
        if (editingId === el.id) return; // let the user place the cursor while editing
        e.preventDefault();
        e.stopPropagation();

        if (e.ctrlKey || e.metaKey) {
            // Toggle membership instead of the normal select-and-drag flow —
            // multi-selected elements aren't individually draggable/resizable
            // (see renderResizeHandles/renderElement), only editable together
            // via the shared toolbar below.
            setEditingId(null);
            setMultiSelected((prev) => {
                const already = prev.some((m) => m.elementId === el.id);
                if (already) return prev.filter((m) => m.elementId !== el.id);
                // First ctrl-click while a single element was already selected
                // normally: fold it in as the starting member of the group
                // instead of losing it.
                const base = prev.length === 0 && selected && selected.elementId !== el.id ? [selected] : prev;
                return [...base, { sectionId, slideId, elementId: el.id }];
            });
            setSelected(null);
            return;
        }
        if (multiSelected.length > 0) setMultiSelected([]);

        const canvas = (e.currentTarget as HTMLElement).closest(".hc-canvas") as HTMLElement;
        if (!canvas) return;
        dragRef.current = {
            sectionId,
            slideId,
            elementId: el.id,
            isText: !el.kind || el.kind === "text",
            startX: e.clientX,
            startY: e.clientY,
            origX: el.x,
            origY: el.y,
            rect: canvas.getBoundingClientRect(),
            moved: false,
        };

        const onMove = (ev: MouseEvent) => {
            const d = dragRef.current;
            if (!d) return;
            const dx = ev.clientX - d.startX;
            const dy = ev.clientY - d.startY;
            if (!d.moved && Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
            d.moved = true;
            const x = Math.min(95, Math.max(0, d.origX + (dx / d.rect.width) * 100));
            const y = Math.min(95, Math.max(0, d.origY + (dy / d.rect.height) * 100));
            updateElement(d.sectionId, d.slideId, d.elementId, { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 });
        };

        const onUp = () => {
            const d = dragRef.current;
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
            if (!d) return;
            if (!d.moved) {
                // plain click: first click selects, click on selected text starts inline editing
                if (selected?.elementId === d.elementId && d.isText) {
                    // Snapshot the text as of edit start: while editing, the span's
                    // children render from this frozen ref (NOT el.text), so the
                    // live state commits on every keystroke can't make React
                    // rewrite the DOM text node and throw the cursor to the start.
                    editingInitialTextRef.current = findElementIn(d.sectionId, d.slideId, d.elementId)?.text || "";
                    setEditingId(d.elementId);
                } else {
                    setSelected({ sectionId: d.sectionId, slideId: d.slideId, elementId: d.elementId });
                    setEditingId(null);
                }
            } else {
                setSelected({ sectionId: d.sectionId, slideId: d.slideId, elementId: d.elementId });
            }
            dragRef.current = null;
        };

        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
    };

    // Click-drag on empty canvas background: draws a selection rectangle and,
    // on release, multi-selects every element it overlaps (mirrors the
    // ctrl/cmd+click toggle in onElementMouseDown, but by area instead of one
    // at a time). Ctrl/Cmd held while dragging ADDS to the current selection
    // instead of replacing it, same convention as the click toggle. A plain
    // click with no drag falls through to the existing deselect behavior.
    const onCanvasBackgroundMouseDown = (e: React.MouseEvent, sectionId: string, slideId: string | null) => {
        if (e.target !== e.currentTarget) return; // a child element handles its own mousedown
        const canvas = e.currentTarget as HTMLElement;
        const canvasRect = canvas.getBoundingClientRect();
        const additive = e.ctrlKey || e.metaKey;
        const startX = e.clientX;
        const startY = e.clientY;
        let moved = false;

        const onMove = (ev: MouseEvent) => {
            const dx = ev.clientX - startX;
            const dy = ev.clientY - startY;
            if (!moved && Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
            moved = true;
            setMarqueeRect({
                sectionId,
                slideId,
                left: Math.min(startX, ev.clientX) - canvasRect.left,
                top: Math.min(startY, ev.clientY) - canvasRect.top,
                width: Math.abs(dx),
                height: Math.abs(dy),
            });
        };

        const onUp = (ev: MouseEvent) => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
            setMarqueeRect(null);
            if (!moved) {
                if (!additive) clearSelection();
                return;
            }
            const x1 = Math.min(startX, ev.clientX);
            const y1 = Math.min(startY, ev.clientY);
            const x2 = Math.max(startX, ev.clientX);
            const y2 = Math.max(startY, ev.clientY);
            const hits: { sectionId: string; slideId: string | null; elementId: string }[] = [];
            canvas.querySelectorAll<HTMLElement>("[data-element-id]").forEach((node) => {
                const r = node.getBoundingClientRect();
                if (r.left < x2 && r.right > x1 && r.top < y2 && r.bottom > y1) {
                    const id = node.getAttribute("data-element-id");
                    if (id) hits.push({ sectionId, slideId, elementId: id });
                }
            });
            if (hits.length === 0) {
                if (!additive) clearSelection();
                return;
            }
            setSelected(null);
            setEditingId(null);
            setMultiSelected((prev) => {
                if (!additive) return hits;
                const merged = [...prev];
                hits.forEach((h) => { if (!merged.some((p) => p.elementId === h.elementId)) merged.push(h); });
                return merged;
            });
        };

        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
    };

    const renderMarqueeOverlay = (sectionId: string, slideId: string | null) => {
        if (!marqueeRect || marqueeRect.sectionId !== sectionId || marqueeRect.slideId !== slideId) return null;
        return (
            <div
                style={{
                    position: "absolute",
                    left: marqueeRect.left,
                    top: marqueeRect.top,
                    width: marqueeRect.width,
                    height: marqueeRect.height,
                    background: "rgba(59,130,246,0.15)",
                    border: "1px solid #3b82f6",
                    pointerEvents: "none",
                    zIndex: 50,
                }}
            />
        );
    };

    // Drag-resize via corner/edge handles. Box: x/y/width(%)/height(px) all free.
    // Image: width(%) only (height follows automatically). Icon: uniform size(px).
    const onResizeMouseDown = (
        e: React.MouseEvent,
        sectionId: string,
        slideId: string | null,
        el: BuilderEl,
        direction: string
    ) => {
        e.preventDefault();
        e.stopPropagation();
        const canvas = (e.currentTarget as HTMLElement).closest(".hc-canvas") as HTMLElement;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        // For an image, orig.width always starts from the ACTUAL current
        // rendered width as a % of the canvas — regardless of whether the
        // stored width is unset (auto), px, or already % — so dragging
        // continues smoothly from wherever the image visually is right now,
        // never jumping. The drag itself always works in % (see below), so
        // this is also what it converts auto/px into once dragging starts.
        let origImageWidthPct = el.width || 20;
        if (el.kind === "image") {
            const img = document.querySelector<HTMLImageElement>(`[data-element-id="${el.id}"] img`);
            if (img) origImageWidthPct = (img.getBoundingClientRect().width / rect.width) * 100;
        }
        const orig = { x: el.x, y: el.y, width: origImageWidthPct, height: el.height || 100, size: el.size || 48, fontSize: el.fontSize || 16 };
        const startX = e.clientX;
        const startY = e.clientY;

        const onMove = (ev: MouseEvent) => {
            const dx = ev.clientX - startX;
            const dy = ev.clientY - startY;
            const dxPct = (dx / rect.width) * 100;
            const dyPct = (dy / rect.height) * 100;

            if (el.kind === "icon") {
                const newSize = Math.min(300, Math.max(16, Math.round(orig.size + (dx + dy) / 2)));
                updateElement(sectionId, slideId, el.id, { size: newSize });
                return;
            }

            // text/button: the corner dot scales the font size (their box just
            // follows the text), diagonal drag = bigger/smaller
            if (el.kind === "text" || el.kind === "button" || !el.kind) {
                const newFont = Math.min(120, Math.max(10, Math.round(orig.fontSize + (dx + dy) / 4)));
                updateElement(sectionId, slideId, el.id, { fontSize: newFont });
                return;
            }

            if (el.kind === "image") {
                let newWidth = orig.width;
                let newX = orig.x;
                // Dragging always works in %, converting away from
                // auto/px if that was the current mode (the numeric field
                // is how you go back to those with precision).
                const patch: Partial<BuilderEl> = {};
                if (direction.includes("e") || direction.includes("w")) {
                    if (direction.includes("e")) newWidth = orig.width + dxPct;
                    if (direction.includes("w")) {
                        newWidth = orig.width - dxPct;
                        newX = orig.x + dxPct;
                    }
                    newWidth = Math.min(100, Math.max(5, newWidth));
                    newX = Math.min(98, Math.max(0, newX));
                    patch.width = Math.round(newWidth * 10) / 10;
                    patch.widthUnit = "%";
                    patch.x = Math.round(newX * 10) / 10;
                }
                // Only the corner/side handles that include n/s touch height —
                // dragging one of those is what opts into a fixed (cropped)
                // height; a pure e/w drag keeps following the natural
                // aspect-ratio height, same as before this control existed.
                if (direction.includes("s") || direction.includes("n")) {
                    let newHeightPx = orig.height + (direction.includes("n") ? -dy : dy);
                    patch.height = Math.max(20, Math.round(newHeightPx));
                    patch.heightUnit = "px";
                }
                updateElement(sectionId, slideId, el.id, patch);
                return;
            }

            // box: free resize on both axes, anchored to the opposite edge
            let newX = orig.x;
            let newY = orig.y;
            let newWidth = orig.width;
            let newHeightPx = orig.height;

            if (direction.includes("e")) newWidth = orig.width + dxPct;
            if (direction.includes("w")) {
                newWidth = orig.width - dxPct;
                newX = orig.x + dxPct;
            }
            if (direction.includes("s")) newHeightPx = orig.height + dy;
            if (direction.includes("n")) {
                newHeightPx = orig.height - dy;
                newY = orig.y + dyPct;
            }

            newWidth = Math.min(100, Math.max(5, newWidth));
            newHeightPx = Math.max(20, newHeightPx);
            newX = Math.min(98, Math.max(0, newX));
            newY = Math.min(98, Math.max(0, newY));

            updateElement(sectionId, slideId, el.id, {
                x: Math.round(newX * 10) / 10,
                y: Math.round(newY * 10) / 10,
                width: Math.round(newWidth * 10) / 10,
                height: Math.round(newHeightPx),
            });
        };

        const onUp = () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
        };

        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
    };

    const RESIZE_CURSORS: Record<string, string> = {
        n: "ns-resize", s: "ns-resize", e: "ew-resize", w: "ew-resize",
        ne: "nesw-resize", sw: "nesw-resize", nw: "nwse-resize", se: "nwse-resize",
    };

    const resizeHandleStyle = (direction: string): React.CSSProperties => {
        const style: React.CSSProperties = {
            position: "absolute",
            width: "10px",
            height: "10px",
            background: "#ffffff",
            border: "2px solid #3b82f6",
            borderRadius: "2px",
            cursor: RESIZE_CURSORS[direction],
            zIndex: 5,
        };
        if (direction.includes("n")) style.top = "-5px";
        if (direction.includes("s")) style.bottom = "-5px";
        if (direction.includes("e")) style.right = "-5px";
        if (direction.includes("w")) style.left = "-5px";
        if (direction === "n" || direction === "s") { style.left = "50%"; style.transform = "translateX(-50%)"; }
        if (direction === "e" || direction === "w") { style.top = "50%"; style.transform = "translateY(-50%)"; }
        return style;
    };

    const renderResizeHandles = (sec: Section, slideId: string | null, el: BuilderEl) => {
        if (selected?.elementId !== el.id || editingId === el.id) return null;
        let directions: string[] = [];
        if (el.kind === "box" || el.kind === "video") directions = ["nw", "n", "ne", "w", "e", "sw", "s", "se"];
        else if (el.kind === "image") directions = ["nw", "w", "sw", "ne", "e", "se"];
        else if (el.kind === "icon") directions = ["se"];
        else if (el.kind === "text" || el.kind === "button" || !el.kind) directions = ["se"];
        else return null;
        return directions.map((dir) => (
            <div
                key={dir}
                style={resizeHandleStyle(dir)}
                onMouseDown={(e) => onResizeMouseDown(e, sec.id, slideId, el, dir)}
            />
        ));
    };

    // While a text element is being edited (contentEditable), the typed text
    // lives only in the DOM — React state still has the old value until the
    // blur commit fires. If Save/Preview runs before that commit lands, it
    // would read (and persist/show) the stale state text even though the
    // canvas visually shows the new one. Track the latest typed text here on
    // every input and flush it into state before saving/previewing.
    const pendingTextRef = useRef<{ sectionId: string; slideId: string | null; elementId: string; text: string } | null>(null);

    // Pure version for synchronous use: setState from a flush isn't visible in
    // the same tick, so handleSave applies the pending edit to its own local
    // copies instead of relying on the state update landing first.
    const applyPendingTextToSection = (sec: Section): Section => {
        const pending = pendingTextRef.current;
        if (!pending || pending.sectionId !== sec.id) return sec;
        const text = pending.text.trim() || "Text";
        if (pending.slideId) {
            return {
                ...sec,
                slides: sec.slides.map((sl) =>
                    sl.id === pending.slideId
                        ? { ...sl, elements: sl.elements.map((el) => (el.id === pending.elementId ? { ...el, text } : el)) }
                        : sl
                ),
            };
        }
        return { ...sec, elements: sec.elements.map((el) => (el.id === pending.elementId ? { ...el, text } : el)) };
    };

    const flushPendingTextEdit = () => {
        const pending = pendingTextRef.current;
        if (!pending) return;
        pendingTextRef.current = null;
        const text = pending.text.trim();
        updateElement(pending.sectionId, pending.slideId, pending.elementId, { text: text || "Text" });
    };

    const commitEditedText = (e: React.FocusEvent<HTMLSpanElement>, sectionId: string, slideId: string | null, elementId: string) => {
        pendingTextRef.current = null;
        const text = (e.currentTarget.innerText || "").trim();
        updateElement(sectionId, slideId, elementId, { text: text || "Text" });
        setEditingId(null);
    };

    // ---------- section list helpers ----------

    const moveSection = (index: number, dir: -1 | 1) => {
        setSections((prev) => {
            const next = [...prev];
            const target = index + dir;
            if (target < 0 || target >= next.length) return prev;
            [next[index], next[target]] = [next[target], next[index]];
            return next;
        });
    };

    const removeSection = (sectionId: string) => {
        setSections((prev) => prev.filter((s) => s.id !== sectionId));
        if (selected?.sectionId === sectionId) {
            setSelected(null);
            setEditingId(null);
        }
    };

    const addSlide = (sec: Section) => {
        const slide = newSlide();
        updateSection(sec.id, { slides: [...sec.slides, slide] });
        setActiveSlide((prev) => ({ ...prev, [sec.id]: sec.slides.length }));
    };

    const removeSlide = (sec: Section) => {
        if (sec.slides.length <= 1) return;
        const idx = activeSlide[sec.id] || 0;
        updateSection(sec.id, { slides: sec.slides.filter((_, i) => i !== idx) });
        setActiveSlide((prev) => ({ ...prev, [sec.id]: Math.max(0, idx - 1) }));
        setSelected(null);
        setEditingId(null);
    };

    const changeSlide = (sec: Section, dir: -1 | 1) => {
        const idx = activeSlide[sec.id] || 0;
        const next = (idx + dir + sec.slides.length) % sec.slides.length;
        setActiveSlide((prev) => ({ ...prev, [sec.id]: next }));
        setSelected(null);
        setEditingId(null);
    };

    const setSlideBgColor = (sec: Section, color: string) => {
        const idx = activeSlide[sec.id] || 0;
        updateSection(sec.id, {
            slides: sec.slides.map((sl, i) => (i === idx ? { ...sl, bgColor: color } : sl)),
        });
    };

    const toggleSlideBgFixed = (sec: Section) => {
        const idx = activeSlide[sec.id] || 0;
        updateSection(sec.id, {
            slides: sec.slides.map((sl, i) => (i === idx ? { ...sl, bgFixed: !sl.bgFixed } : sl)),
        });
    };

    // ---------- background picker ----------

    const openBgPicker = (sec: Section) => {
        if (sec.type === "slider") {
            const idx = activeSlide[sec.id] || 0;
            const slide = sec.slides[idx];
            if (!slide) return;
            setBgPicker({ sectionId: sec.id, slideId: slide.id, allowTemplates: true });
        } else {
            setBgPicker({ sectionId: sec.id, slideId: null, allowTemplates: false });
        }
    };

    const applyBgTemplate = (index: number) => {
        if (!bgPicker) return;
        if (bgPicker.slideId) {
            mutateSectionById(bgPicker.sectionId, (sec) => ({
                ...sec,
                slides: sec.slides.map((sl) =>
                    sl.id === bgPicker.slideId ? { ...sl, bgUrl: "", bgColor: "", bgIndex: index } : sl
                ),
            }));
        }
        setBgPicker(null);
    };

    const applyBgImage = (url: string) => {
        if (!bgPicker) return;
        if (bgPicker.slideId) {
            mutateSectionById(bgPicker.sectionId, (sec) => ({
                ...sec,
                slides: sec.slides.map((sl) =>
                    sl.id === bgPicker.slideId ? { ...sl, bgUrl: url, bgColor: "" } : sl
                ),
            }));
        } else {
            updateSection(bgPicker.sectionId, { bgUrl: url });
        }
        setBgPicker(null);
    };

    const uploadFromPicker = () => {
        if (!bgPicker) return;
        uploadTargetRef.current = bgPicker.slideId
            ? { purpose: "slidebg", sectionId: bgPicker.sectionId, slideId: bgPicker.slideId }
            : { purpose: "sectionbg", sectionId: bgPicker.sectionId };
        fileInputRef.current?.click();
    };

    // ---------- image upload ----------

    const openImagePicker = (purpose: UploadPurpose, sec: Section) => {
        if (purpose === "imageel" && sec.type === "slider") {
            const idx = activeSlide[sec.id] || 0;
            const slide = sec.slides[idx];
            if (!slide) return;
            uploadTargetRef.current = { purpose, sectionId: sec.id, slideId: slide.id };
        } else {
            uploadTargetRef.current = { purpose, sectionId: sec.id };
        }
        fileInputRef.current?.click();
    };

    // Whole-page backdrop image — not tied to any one section, so sectionId
    // here is just a key for the shared uploadingFor loading-state, not a
    // real section lookup.
    const openPageBgImagePicker = () => {
        uploadTargetRef.current = { purpose: "pagebg", sectionId: "__page__" };
        fileInputRef.current?.click();
    };

    // Per-section "cover image" (Feature: full-width lead image shown above a
    // section's own content, toggled on/off, animates in on scroll — see
    // sec.coverImageUrl/coverImageEnabled and BuilderSections.tsx).
    const openCoverImagePicker = (sec: Section) => {
        uploadTargetRef.current = { purpose: "coverimage", sectionId: sec.id };
        fileInputRef.current?.click();
    };

    // Sets a fill image on the currently selected shape (box) element
    const openShapeFillPicker = () => {
        if (!selected) return;
        uploadTargetRef.current = {
            purpose: "shapefill",
            sectionId: selected.sectionId,
            slideId: selected.slideId || undefined,
            elementId: selected.elementId,
        };
        fileInputRef.current?.click();
    };

    const handleImageSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        const target = uploadTargetRef.current;
        if (!file || !target) return;

        try {
            setUploadingFor(target.sectionId);
            const formData = new FormData();
            formData.append("media", file);
            const response = await apiFunctions.post("/homecontent/upload/homecontent", formData, null, true);

            if (response?.data?.success && response.data.url) {
                const url = response.data.url;
                if (target.purpose === "slidebg") {
                    mutateSectionById(target.sectionId, (sec) => ({
                        ...sec,
                        slides: sec.slides.map((sl) =>
                            sl.id === target.slideId ? { ...sl, bgUrl: url, bgColor: "" } : sl
                        ),
                    }));
                    setBgPicker(null);
                } else if (target.purpose === "sectionbg") {
                    updateSection(target.sectionId, { bgUrl: url });
                    setBgPicker(null);
                } else if (target.purpose === "shapefill" && target.elementId) {
                    updateElement(target.sectionId, target.slideId || null, target.elementId, { url });
                } else if (target.purpose === "pagebg") {
                    setPageBgImage(url);
                } else if (target.purpose === "coverimage") {
                    updateSection(target.sectionId, { coverImageUrl: url, coverImageEnabled: true });
                } else {
                    const sec = findSection(target.sectionId);
                    if (sec) addElementToSection(sec, newImageElement(url));
                }
                loadUploadedImages();
                showToast({ severity: "success", summary: "Success", detail: "Image uploaded", life: 3000 });
            } else {
                showToast({
                    severity: "error",
                    summary: "Error",
                    detail: response?.data?.message || "Image upload failed",
                    life: 3000,
                });
            }
        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            setUploadingFor(null);
            uploadTargetRef.current = null;
        }
    };

    // ---------- save ----------

    const handleSave = async () => {
        // Apply any still-uncommitted inline text edit to local copies FIRST —
        // the state update from flushPendingTextEdit below won't be visible to
        // this function's closure in the same tick.
        const sectionsToSave = sections.map(applyPendingTextToSection);
        const headerToSave = applyPendingTextToSection(headerSec);
        const footerToSave = applyPendingTextToSection(footerSec);
        flushPendingTextEdit();

        try {
            setSaving(true);

            // A subpage saves only its own content — header/footer are
            // site-wide and only editable (and saved) on the Home page tab.
            if (activePageId) {
                const pageRes = await apiFunctions.put(`/sitepage/${activePageId}`, {
                    sections: sectionsToSave,
                    pagebg: pageBg,
                    pagebgimage: pageBgImage,
                    pagebgimagefixed: pageBgImageFixed,
                    showinstallbanner: showInstallBanner,
                    metatitle: metaTitle,
                    metadescription: metaDescription,
                    metakeywords: metaKeywords,
                });
                if (pageRes?.data?.success) {
                    showToast({ severity: "success", summary: "Success", detail: pageRes.data.message, life: 3000 });
                } else {
                    showToast({ severity: "error", summary: "Error", detail: pageRes?.data?.message || "Something went wrong", life: 3000 });
                }
                return;
            }

            const response = await apiFunctions.put("/homecontent", {
                sections: sectionsToSave,
                pagebg: pageBg,
                pagebgimage: pageBgImage,
                pagebgimagefixed: pageBgImageFixed,
                showinstallbanner: showInstallBanner,
                header: { ...headerCfg, custom: headerToSave },
                footer: { ...footerCfg, custom: footerToSave },
            });

            if (response?.data?.success) {
                // Any uploaded image no longer referenced anywhere in the saved content
                // (background removed, or the element/section that used it got deleted)
                // gets its file removed from disk too, so uploads don't just pile up.
                const currentImageUrls = collectImageUrls(sectionsToSave, headerToSave, footerToSave);
                const removedUrls = Array.from(savedImageUrlsRef.current).filter((url) => !currentImageUrls.has(url));
                if (removedUrls.length > 0) {
                    await Promise.all(
                        removedUrls.map((url) => {
                            const fileName = ownedImageFileName(url);
                            return fileName ? apiFunctions.delete(`/homecontent/images/${fileName}`).catch(() => { }) : Promise.resolve();
                        })
                    );
                    loadUploadedImages();
                }
                savedImageUrlsRef.current = currentImageUrls;

                showToast({
                    severity: "success",
                    summary: "Success",
                    detail: response.data.message,
                    life: 3000,
                });
            } else {
                showToast({
                    severity: "error",
                    summary: "Error",
                    detail: response?.data?.message || "Something went wrong",
                    life: 3000,
                });
            }
        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            setSaving(false);
        }
    };

    // ---------- element rendering (editable) ----------

    // staticFlow: renders the element at its natural position within whatever
    // wrapper placed it, instead of absolute left/top — used for a caption
    // grouped directly under an image (see the imageCaptionGroups heuristic
    // below), so it stays glued to the image instead of independently
    // computing its own y% of the (possibly much taller) section.
    const renderElement = (sec: Section, slideId: string | null, el: BuilderEl, opts?: { staticFlow?: boolean }) => {
        const isSelected = selected?.elementId === el.id || multiSelected.some((m) => m.elementId === el.id);
        const isEditing = editingId === el.id;
        const isMultiMember = multiSelected.some((m) => m.elementId === el.id);
        const outline = isEditing
            ? "2px solid #22c55e"
            : isMultiMember
                ? "2px dashed #f59e0b"
                : isSelected
                    ? "2px dashed #3b82f6"
                    : "1px dashed transparent";
        const common: React.CSSProperties = opts?.staticFlow ? {
            // relative (not the default static): still an in-flow box sized by
            // the wrapper that placed it, but stays its own positioning context
            // so its resize handles (position: absolute) anchor to itself
            // instead of drifting up to the group wrapper.
            position: "relative",
            cursor: "move",
            outline,
            outlineOffset: "2px",
        } : {
            position: "absolute",
            left: `${el.x}%`,
            top: `${el.y}%`,
            cursor: "move",
            outline,
            outlineOffset: "2px",
            userSelect: "none",
        };
        // A staticFlow element (grouped caption/image) is already counted via
        // its group wrapper's own data-el-item — tagging it too would
        // double-count its height at its OWN (pre-grouping) y%, inflating the
        // measured section height.
        const measurementProps = opts?.staticFlow ? {} : { "data-el-item": true, "data-el-y": el.y || 0 };

        if (el.kind === "image") {
            // A grouped (staticFlow) image sits inside an auto-width flex
            // wrapper, not directly in the section — a percentage width there
            // has no reliable containing block to resolve against (and
            // happened to fall back to the raw image file's own pixel size,
            // which only looked "correct" by accident). Match the live
            // site/Preview instead (BuilderSections.tsx's constantSize): the
            // same width% resolved against the fixed 1440px design canvas, as
            // a real px value, so editor and live always show the same size.
            const resolvedImageWidth = imageWidthStyle(el, opts?.staticFlow);
            // The inner <img> is normally 100% of the sized wrapper — but for
            // "auto" (no override), the wrapper's own width is ALSO "auto"
            // (shrink-to-fit), and a percentage width on the child of an
            // auto-width box doesn't resolve either (the exact same
            // indeterminate-container problem staticFlow captions hit
            // earlier) — so auto needs the inner <img> at its own natural
            // width too, not 100% of an undetermined wrapper.
            const innerImageWidth = resolvedImageWidth === "auto" ? "auto" : "100%";
            return (
                <div
                    key={el.id}
                    data-element-id={el.id}
                    {...measurementProps}
                    onMouseDown={(e) => onElementMouseDown(e, sec.id, slideId, el)}
                    style={{ ...common, width: resolvedImageWidth, ...getShadowStyle("image", el.shadow) }}
                >
                    <img
                        src={el.url}
                        alt=""
                        draggable={false}
                        style={{
                            width: innerImageWidth,
                            display: "block",
                            ...imageHeightStyle(el),
                        }}
                    />
                    {renderResizeHandles(sec, slideId, el)}
                </div>
            );
        }

        if (el.kind === "icon") {
            const Icon = ICON_MAP[el.icon || "star"] || StarIcon;
            return (
                <span
                    key={el.id}
                    data-element-id={el.id}
                    {...measurementProps}
                    onMouseDown={(e) => onElementMouseDown(e, sec.id, slideId, el)}
                    style={{ ...common, display: "inline-block", lineHeight: 0 }}
                >
                    <Icon
                        style={{ width: `${el.size || 48}px`, height: `${el.size || 48}px`, color: el.color || "#ffffff", ...getShadowStyle("icon", el.shadow) }}
                    />
                    {renderResizeHandles(sec, slideId, el)}
                </span>
            );
        }

        if (el.kind === "box") {
            return (
                <div
                    key={el.id}
                    data-element-id={el.id}
                    {...measurementProps}
                    onMouseDown={(e) => onElementMouseDown(e, sec.id, slideId, el)}
                    style={{
                        ...common,
                        width: `${el.width || 20}%`,
                        height: `${el.height || 100}px`,
                        ...shapeFillStyle(el),
                        transform: el.rotation ? `rotate(${el.rotation}deg)` : undefined,
                    }}
                >
                    {el.url && (
                        <img src={el.url} alt="" draggable={false} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                    )}
                    {renderResizeHandles(sec, slideId, el)}
                </div>
            );
        }

        if (el.kind === "video") {
            return (
                <div
                    key={el.id}
                    data-element-id={el.id}
                    {...measurementProps}
                    onMouseDown={(e) => onElementMouseDown(e, sec.id, slideId, el)}
                    style={{
                        ...common,
                        width: `${el.width || 70}%`,
                        height: `${el.height || 260}px`,
                        background: "rgba(255,255,255,0.06)",
                        border: "1px dashed rgba(255,255,255,0.4)",
                        borderRadius: "8px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "6px",
                        color: "#e5e7eb",
                    }}
                >
                    <VideoCameraIcon style={{ width: "32px", height: "32px", color: "#22c55e" }} />
                    <span style={{ fontSize: "13px" }}>
                        {liveVideos.length > 0 ? `${liveVideos.length} active video(s)` : "No active live videos"}
                    </span>
                    {renderResizeHandles(sec, slideId, el)}
                </div>
            );
        }

        if (el.kind === "button") {
            const BtnIcon = el.icon ? ICON_MAP[el.icon] : null;
            const btnIconNode = BtnIcon ? (
                <BtnIcon style={{ width: `${(el.fontSize || 16) + 2}px`, height: `${(el.fontSize || 16) + 2}px`, flexShrink: 0 }} />
            ) : null;
            return (
                <span
                    key={el.id}
                    data-element-id={el.id}
                    {...measurementProps}
                    onMouseDown={(e) => onElementMouseDown(e, sec.id, slideId, el)}
                    style={{
                        ...common,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: btnIconNode ? "8px" : undefined,
                        background: el.bg || "#22c55e",
                        color: el.color || "#ffffff",
                        padding: "10px 24px",
                        borderRadius: `${el.radius ?? 8}px`,
                        fontSize: `${el.fontSize || 16}px`,
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        width: el.width ? imageWidthStyle(el, opts?.staticFlow) : undefined,
                        ...boxHeightStyle(el),
                        ...getShadowStyle("button", el.shadow),
                    }}
                >
                    {el.iconPos !== "right" && btnIconNode}
                    {el.text || "Button"}
                    {el.iconPos === "right" && btnIconNode}
                    {renderResizeHandles(sec, slideId, el)}
                </span>
            );
        }

        return (
            <span
                key={el.id}
                data-element-id={el.id}
                {...measurementProps}
                contentEditable={isEditing}
                suppressContentEditableWarning
                onMouseDown={(e) => onElementMouseDown(e, sec.id, slideId, el)}
                onInput={isEditing ? (e) => {
                    // Live-commit every keystroke into state. Relying on blur alone
                    // loses the edit whenever blur never fires (e.g. mousedown on
                    // another element preventDefaults the focus change) — state
                    // then kept the old text while the canvas DOM showed the new
                    // one, so Preview/Save quietly used stale text. The span's
                    // children stay frozen (editingInitialTextRef) so this can't
                    // move the cursor.
                    pendingTextRef.current = null;
                    updateElement(sec.id, slideId, el.id, { text: (e.currentTarget as HTMLElement).innerText || "" });
                } : undefined}
                onBlur={isEditing ? (e) => commitEditedText(e, sec.id, slideId, el.id) : undefined}
                ref={(node) => {
                    if (node && isEditing && document.activeElement !== node) node.focus();
                }}
                style={{
                    ...common,
                    fontSize: `${el.fontSize}px`,
                    color: el.color,
                    fontWeight: el.bold ? 700 : 400,
                    fontStyle: el.italic ? "italic" : "normal",
                    // A grouped caption (staticFlow) is a short one-liner that
                    // must stay on one line at its natural width — the normal
                    // 80% maxWidth is a % of the WHOLE section, which resolves
                    // against the much narrower auto-width group wrapper
                    // instead and wraps a short name like "ADVOCATE YOGESH
                    // PIPROTAR" onto two lines. Matches the live site/Preview
                    // (BuilderSections.tsx), which already does this.
                    whiteSpace: opts?.staticFlow ? "nowrap" : "pre-wrap",
                    // A grouped caption ignores any width override and keeps the
                    // single-line/natural-width behavior above — same invariant
                    // as maxWidth/nowrap for that case.
                    width: !opts?.staticFlow && el.width ? imageWidthStyle(el, opts?.staticFlow) : undefined,
                    maxWidth: opts?.staticFlow ? undefined : (el.width ? undefined : "80%"),
                    cursor: isEditing ? "text" : "move",
                    userSelect: isEditing ? "text" : "none",
                    minWidth: "20px",
                    minHeight: "1em",
                    display: "inline-block",
                    ...boxHeightStyle(el),
                    ...getShadowStyle("text", el.shadow),
                }}
            >
                {isEditing ? editingInitialTextRef.current : el.text}
                {renderResizeHandles(sec, slideId, el)}
            </span>
        );
    };

    // ---------- toolbar ----------

    // The toolbar's "current" element — either the single selection, or (in a
    // multi-select) the first one chosen, purely to decide which controls to
    // show and what values to display in them. Editing any control writes
    // through applyToSelection to EVERY selected element, not just this one.
    const primarySelection = activeSelection[0] || null;
    const selectedElement = primarySelection
        ? findElementIn(primarySelection.sectionId, primarySelection.slideId, primarySelection.elementId)
        : null;
    const selectedKind: ElementKind = selectedElement?.kind || "text";

    // Fields not relevant to a given kind (e.g. fontSize on an image) are
    // simply ignored at render time by that kind, so applying the primary
    // element's toolbar blindly to a mixed-kind multi-selection is harmless —
    // it just has no visible effect on the elements that don't use that field.
    const applyToSelection = (patch: Partial<BuilderEl>) => {
        activeSelection.forEach((t) => updateElement(t.sectionId, t.slideId, t.elementId, patch));
    };

    // An image with no explicit Height set follows its natural aspect ratio —
    // show what that currently resolves to as the field's placeholder (e.g.
    // "e.g. 243"), instead of a bare, ambiguous "0", by reading the real
    // rendered height of the actual <img> on the canvas.
    const [autoImageHeightPx, setAutoImageHeightPx] = useState<number | null>(null);
    useEffect(() => {
        if (!selected || selectedKind !== "image" || selectedElement?.height) {
            setAutoImageHeightPx(null);
            return;
        }
        const elementId = selected.elementId;
        const measure = () => {
            const img = document.querySelector<HTMLImageElement>(`[data-element-id="${elementId}"] img`);
            if (img) setAutoImageHeightPx(Math.round(img.getBoundingClientRect().height));
        };
        const raf = requestAnimationFrame(measure);
        return () => cancelAnimationFrame(raf);
    }, [selected, selectedKind, selectedElement?.width, selectedElement?.height]);

    const toolbarBtn = (active: boolean): React.CSSProperties => ({
        fontSize: "15px",
        padding: "4px 12px",
        borderRadius: "4px",
        border: "none",
        cursor: "pointer",
        background: active ? "#3b82f6" : "#e5e7eb",
        color: active ? "#fff" : "#111827",
    });

    const numInput = (value: number, min: number, max: number, onChange: (v: number) => void, width = 64) => (
        <NumberInput value={value} min={min} max={max} onChange={onChange} width={width} />
    );

    // Same Auto/px/%-with-live-measurement pattern as the image Width control
    // above, generalized for kinds (text, button) whose box is content-sized
    // by default rather than image-intrinsic-sized — measures the element's
    // own rendered box directly instead of a nested <img>.
    const renderWidthControl = (elementId: string, sectionId: string, slideId: string | null, width: number | undefined, widthUnit: "px" | "%" | undefined) => {
        const measurePx = () => {
            const node = document.querySelector<HTMLElement>(`[data-element-id="${elementId}"]`);
            return node ? Math.round(node.getBoundingClientRect().width) : width || 100;
        };
        const measurePercent = () => {
            const canvasEl = document.querySelector(".hc-canvas");
            const node = document.querySelector<HTMLElement>(`[data-element-id="${elementId}"]`);
            const canvasWidth = canvasEl?.getBoundingClientRect().width;
            return node && canvasWidth ? Math.round((node.getBoundingClientRect().width / canvasWidth) * 100) : width || 30;
        };
        return width ? (
            <>
                <button type="button" onClick={() => { if ((widthUnit || "%") === "px") return; updateElement(sectionId, slideId, elementId, { widthUnit: "px", width: measurePx() }); }} style={toolbarBtn(widthUnit === "px")}>px</button>
                <button type="button" onClick={() => { if ((widthUnit || "%") === "%") return; updateElement(sectionId, slideId, elementId, { widthUnit: "%", width: measurePercent() }); }} style={toolbarBtn((widthUnit || "%") === "%")}>%</button>
                {numInput(width, 1, widthUnit === "px" ? 2000 : 100, (v) => updateElement(sectionId, slideId, elementId, { width: v }), 64)}
                <button type="button" onClick={() => updateElement(sectionId, slideId, elementId, { width: undefined, widthUnit: undefined })} style={toolbarBtn(false)}>Auto</button>
            </>
        ) : (
            <button type="button" onClick={() => updateElement(sectionId, slideId, elementId, { width: measurePercent(), widthUnit: "%" })} style={toolbarBtn(true)}>Auto</button>
        );
    };

    // Height counterpart — px only (no aspect-ratio option, unlike image:
    // there's no "own width" to be a ratio of for text/button box height).
    const renderHeightControl = (elementId: string, sectionId: string, slideId: string | null, height: number | undefined) => {
        const measurePx = () => {
            const node = document.querySelector<HTMLElement>(`[data-element-id="${elementId}"]`);
            return node ? Math.round(node.getBoundingClientRect().height) : height || 40;
        };
        return height ? (
            <>
                {numInput(height, 1, 2000, (v) => updateElement(sectionId, slideId, elementId, { height: v }), 64)}
                <button type="button" onClick={() => updateElement(sectionId, slideId, elementId, { height: undefined, heightUnit: undefined })} style={toolbarBtn(false)}>Auto</button>
            </>
        ) : (
            <button type="button" onClick={() => updateElement(sectionId, slideId, elementId, { height: measurePx(), heightUnit: "px" })} style={toolbarBtn(true)}>Auto</button>
        );
    };

    const elementToolbar = primarySelection && selectedElement && (
        <div
            className="flex items-center gap-2 flex-wrap p-2 rounded-lg"
            style={{ background: "#1f2937", maxWidth: "calc(100vw - 16px)", boxShadow: "0 8px 24px rgba(0,0,0,0.35)" }}
        >
            {selectedKind === "text" && (
                <>
                    <span className="text-sm text-white">Font</span>
                    {numInput(selectedElement.fontSize || 16, 10, 90, (v) => applyToSelection( { fontSize: v }))}
                    <input
                        type="color"
                        value={selectedElement.color || "#ffffff"}
                        onChange={(e) => applyToSelection( { color: e.target.value })}
                        style={{ width: "38px", height: "30px", border: "none", background: "transparent", cursor: "pointer" }}
                    />
                    <button
                        type="button"
                        onClick={() => applyToSelection( { bold: !selectedElement.bold })}
                        style={{ ...toolbarBtn(!!selectedElement.bold), fontWeight: 700 }}
                    >
                        B
                    </button>
                    <button
                        type="button"
                        onClick={() => applyToSelection( { italic: !selectedElement.italic })}
                        style={{ ...toolbarBtn(!!selectedElement.italic), fontStyle: "italic" }}
                    >
                        I
                    </button>
                    <span className="text-sm text-white">Width</span>
                    {renderWidthControl(primarySelection.elementId, primarySelection.sectionId, primarySelection.slideId, selectedElement.width, selectedElement.widthUnit)}
                    <span className="text-sm text-white">Height</span>
                    {renderHeightControl(primarySelection.elementId, primarySelection.sectionId, primarySelection.slideId, selectedElement.height)}
                </>
            )}
            {selectedKind === "image" && (
                <>
                    {/* Width: unset (the default) is the image's own
                        natural/intrinsic size — shown as a plain "Auto"
                        button, same pattern as Height below. Clicking it
                        switches to manual mode pre-filled with the currently
                        rendered width (in %), so nothing jumps. Half/Full are
                        shortcuts into % mode directly. */}
                    <span className="text-sm text-white">Width</span>
                    {selectedElement.width ? (
                        <>
                            <button
                                type="button"
                                onClick={() => {
                                    if ((selectedElement.widthUnit || "%") === "px") return;
                                    const img = document.querySelector<HTMLImageElement>(`[data-element-id="${primarySelection.elementId}"] img`);
                                    const widthPx = img ? Math.round(img.getBoundingClientRect().width) : selectedElement.width;
                                    applyToSelection( { widthUnit: "px", width: widthPx });
                                }}
                                style={toolbarBtn(selectedElement.widthUnit === "px")}
                            >
                                px
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    if ((selectedElement.widthUnit || "%") === "%") return;
                                    const canvasEl = document.querySelector(".hc-canvas");
                                    const img = document.querySelector<HTMLImageElement>(`[data-element-id="${primarySelection.elementId}"] img`);
                                    const canvasWidth = canvasEl?.getBoundingClientRect().width;
                                    const percent = img && canvasWidth
                                        ? Math.round((img.getBoundingClientRect().width / canvasWidth) * 100)
                                        : selectedElement.width;
                                    applyToSelection( { widthUnit: "%", width: percent });
                                }}
                                style={toolbarBtn((selectedElement.widthUnit || "%") === "%")}
                            >
                                %
                            </button>
                            {numInput(
                                selectedElement.width,
                                1,
                                selectedElement.widthUnit === "px" ? 2000 : 100,
                                (v) => applyToSelection( { width: v }),
                                64
                            )}
                            <button type="button" onClick={() => applyToSelection( { width: 50, widthUnit: "%" })} style={toolbarBtn(selectedElement.width === 50 && (selectedElement.widthUnit || "%") === "%")}>Half</button>
                            <button type="button" onClick={() => applyToSelection( { width: 100, widthUnit: "%", x: 0 })} style={toolbarBtn(selectedElement.width === 100 && (selectedElement.widthUnit || "%") === "%")}>Full</button>
                            <button type="button" onClick={() => applyToSelection( { width: undefined, widthUnit: undefined })} style={toolbarBtn(false)}>Auto</button>
                        </>
                    ) : (
                        <button
                            type="button"
                            onClick={() => {
                                const canvasEl = document.querySelector(".hc-canvas");
                                const img = document.querySelector<HTMLImageElement>(`[data-element-id="${primarySelection.elementId}"] img`);
                                const canvasWidth = canvasEl?.getBoundingClientRect().width;
                                const percent = img && canvasWidth
                                    ? Math.round((img.getBoundingClientRect().width / canvasWidth) * 100)
                                    : 30;
                                applyToSelection( { width: percent, widthUnit: "%" });
                            }}
                            style={toolbarBtn(true)}
                        >
                            Auto
                        </button>
                    )}
                    {/* Height: unset (the default) follows the image's own
                        aspect ratio off Width % — shown as a plain "Auto"
                        button with no number box at all, since there's
                        nothing to type until it's turned on. Clicking it
                        switches to manual mode pre-filled with whatever height
                        is currently in effect (read live off the rendered
                        photo), so the value doesn't jump on switching. Once
                        manual, px is a fixed cropped height; % is relative to
                        the image's own width (e.g. 100% = square) via
                        aspect-ratio, so it stays correct regardless of how
                        wide the image actually renders. */}
                    <span className="text-sm text-white">Height</span>
                    {selectedElement.height ? (
                        <>
                            <button
                                type="button"
                                onClick={() => {
                                    // Convert the number, not just reinterpret it under the
                                    // new unit — 287 meant "287px", so switching to % must
                                    // become "the % that currently renders as ~287px", not
                                    // literally 287% (which is huge). Measured live off the
                                    // actual rendered photo so it's exact either direction.
                                    if ((selectedElement.heightUnit || "px") === "px") return;
                                    const img = document.querySelector<HTMLImageElement>(`[data-element-id="${primarySelection.elementId}"] img`);
                                    const heightPx = img ? Math.round(img.getBoundingClientRect().height) : selectedElement.height;
                                    applyToSelection( { heightUnit: "px", height: heightPx });
                                }}
                                style={toolbarBtn((selectedElement.heightUnit || "px") === "px")}
                            >
                                px
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    if (selectedElement.heightUnit === "%") return;
                                    const img = document.querySelector<HTMLImageElement>(`[data-element-id="${primarySelection.elementId}"] img`);
                                    const rect = img?.getBoundingClientRect();
                                    const percent = rect && rect.width ? Math.round((rect.height / rect.width) * 100) : selectedElement.height;
                                    applyToSelection( { heightUnit: "%", height: percent });
                                }}
                                style={toolbarBtn(selectedElement.heightUnit === "%")}
                            >
                                %
                            </button>
                            {numInput(
                                selectedElement.height,
                                1,
                                selectedElement.heightUnit === "%" ? 500 : 2000,
                                (v) => applyToSelection( { height: v }),
                                64
                            )}
                            <button type="button" onClick={() => applyToSelection( { height: undefined, heightUnit: undefined })} style={toolbarBtn(false)}>Auto</button>
                        </>
                    ) : (
                        <button
                            type="button"
                            onClick={() => applyToSelection( { height: autoImageHeightPx || 100, heightUnit: "px" })}
                            style={toolbarBtn(true)}
                        >
                            Auto
                        </button>
                    )}
                </>
            )}
            {selectedKind === "icon" && (
                <>
                    <span className="text-sm text-white">Icon</span>
                    <IconPicker
                        value={selectedElement.icon || "star"}
                        color={selectedElement.color || "#ffffff"}
                        onChange={(icon) => applyToSelection( { icon })}
                        onColorChange={(color) => applyToSelection( { color })}
                    />
                    <span className="text-sm text-white">Size</span>
                    {numInput(selectedElement.size || 48, 16, 200, (v) => applyToSelection( { size: v }))}
                </>
            )}
            {selectedKind === "box" && (
                <>
                    <span className="text-sm text-white">Shape</span>
                    <select
                        value={selectedElement.shapeType || "rectangle"}
                        onChange={(e) => {
                            const shapeType = e.target.value as ShapeType;
                            // Line starts as a thin bar, not the default 100px-tall
                            // rectangle — admin can still resize/rotate it from there.
                            // Only applied when actually switching TO line (not on
                            // every re-selection) so it doesn't stomp a size the
                            // admin already dialed in.
                            const wasLine = selectedElement.shapeType === "line";
                            applyToSelection( {
                                shapeType,
                                ...(shapeType === "line" && !wasLine ? { height: 4, radius: 0 } : {}),
                            });
                        }}
                        style={{ borderRadius: "4px", border: "none", padding: "4px 6px" }}
                    >
                        {SHAPE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                    <span className="text-sm text-white">Bg</span>
                    <input
                        type="color"
                        value={selectedElement.bg || "#ffffff"}
                        onChange={(e) => applyToSelection( { bg: e.target.value })}
                        style={{ width: "38px", height: "30px", border: "none", background: "transparent", cursor: "pointer" }}
                    />
                    <span className="text-sm text-white">Border</span>
                    <input
                        type="color"
                        value={selectedElement.borderColor || "#000000"}
                        onChange={(e) => applyToSelection( { borderColor: e.target.value })}
                        style={{ width: "38px", height: "30px", border: "none", background: "transparent", cursor: "pointer" }}
                    />
                    {numInput(selectedElement.borderWidth || 0, 0, 20, (v) => applyToSelection( { borderWidth: v }), 50)}
                    <span className="text-sm text-white">W %</span>
                    {numInput(selectedElement.width || 20, 5, 100, (v) => applyToSelection( { width: v }))}
                    <span className="text-sm text-white">H px</span>
                    {numInput(selectedElement.height || 100, 2, 800, (v) => applyToSelection( { height: v }))}
                    {(!selectedElement.shapeType || selectedElement.shapeType === "rectangle") && (
                        <>
                            <span className="text-sm text-white">Radius</span>
                            {numInput(selectedElement.radius ?? 8, 0, 100, (v) => applyToSelection( { radius: v }), 56)}
                        </>
                    )}
                    <span className="text-sm text-white">Rotate</span>
                    {numInput(selectedElement.rotation || 0, -180, 180, (v) => applyToSelection( { rotation: v }), 56)}
                    <Button
                        label={selectedElement.url ? "Change Image" : "Fill Image"}
                        text
                        icon={<PhotoIcon className="w-5 h-5 mr-1" />}
                        loading={uploadingFor === primarySelection.sectionId}
                        onClick={openShapeFillPicker}
                        style={{ color: "#e5e7eb" }}
                    />
                    {selectedElement.url && (
                        <Button
                            label="Remove Image"
                            text
                            style={{ color: "#dc2626" }}
                            onClick={() => applyToSelection( { url: "" })}
                        />
                    )}
                </>
            )}
            {selectedKind === "button" && (
                <>
                    <input
                        type="text"
                        value={selectedElement.text || ""}
                        placeholder="Button text"
                        onChange={(e) => applyToSelection( { text: e.target.value })}
                        style={{ width: "140px", borderRadius: "4px", border: "none", padding: "4px 8px" }}
                    />
                    <span className="text-sm text-white">Font</span>
                    {numInput(selectedElement.fontSize || 16, 10, 40, (v) => applyToSelection( { fontSize: v }), 56)}
                    <span className="text-sm text-white">Bg</span>
                    <input
                        type="color"
                        value={selectedElement.bg || "#22c55e"}
                        onChange={(e) => applyToSelection( { bg: e.target.value })}
                        style={{ width: "38px", height: "30px", border: "none", background: "transparent", cursor: "pointer" }}
                    />
                    <span className="text-sm text-white">Text</span>
                    <input
                        type="color"
                        value={selectedElement.color || "#ffffff"}
                        onChange={(e) => applyToSelection( { color: e.target.value })}
                        style={{ width: "38px", height: "30px", border: "none", background: "transparent", cursor: "pointer" }}
                    />
                    <span className="text-sm text-white">Radius</span>
                    {numInput(selectedElement.radius ?? 8, 0, 50, (v) => applyToSelection( { radius: v }), 56)}
                    <span className="text-sm text-white">Width</span>
                    {renderWidthControl(primarySelection.elementId, primarySelection.sectionId, primarySelection.slideId, selectedElement.width, selectedElement.widthUnit)}
                    <span className="text-sm text-white">Height</span>
                    {renderHeightControl(primarySelection.elementId, primarySelection.sectionId, primarySelection.slideId, selectedElement.height)}
                    <span className="text-sm text-white">Icon</span>
                    <IconPicker
                        value={selectedElement.icon || ""}
                        color={selectedElement.color || "#ffffff"}
                        onChange={(icon) => applyToSelection( { icon, iconPos: selectedElement.iconPos || "left" })}
                        onColorChange={(color) => applyToSelection( { color })}
                    />
                    {selectedElement.icon && (
                        <>
                            <button
                                type="button"
                                title="Icon text ni PEHLA (prefix)"
                                onClick={() => applyToSelection( { iconPos: "left" })}
                                style={toolbarBtn((selectedElement.iconPos || "left") === "left")}
                            >
                                Prefix
                            </button>
                            <button
                                type="button"
                                title="Icon text ni PACHHI (postfix)"
                                onClick={() => applyToSelection( { iconPos: "right" })}
                                style={toolbarBtn(selectedElement.iconPos === "right")}
                            >
                                Postfix
                            </button>
                            <button
                                type="button"
                                title="Icon kadhi nakho"
                                onClick={() => applyToSelection( { icon: "" })}
                                style={{ ...toolbarBtn(false), color: "#fca5a5" }}
                            >
                                ✕
                            </button>
                        </>
                    )}
                </>
            )}
            {selectedKind === "video" && (
                <>
                    <span className="text-sm text-white">W %</span>
                    {numInput(selectedElement.width || 70, 10, 100, (v) => applyToSelection( { width: v }))}
                    <span className="text-sm text-white">H px</span>
                    {numInput(selectedElement.height || 260, 100, 800, (v) => applyToSelection( { height: v }), 56)}
                </>
            )}
            {selectedKind !== "video" && (
                <>
                    <span className="text-sm text-white">Shadow</span>
                    {SHADOW_OPTIONS.map((opt) => (
                        <button
                            key={opt.value}
                            type="button"
                            onClick={() => applyToSelection( { shadow: opt.value as BuilderEl["shadow"] })}
                            style={toolbarBtn((selectedElement.shadow || "none") === opt.value)}
                        >
                            {opt.label}
                        </button>
                    ))}
                </>
            )}
            {selectedKind !== "video" && (
                <>
                    {/* link works on every other element: /local-route or https://external */}
                    <span className="text-sm text-white">Link</span>
                    <input
                        type="text"
                        value={selectedElement.link || ""}
                        placeholder="/enregister or https://..."
                        onChange={(e) => applyToSelection( { link: e.target.value.trim() })}
                        onBlur={(e) => {
                            // A linked text/button in the HEADER is (usually) a nav menu
                            // item — ask once when the link is first set whether it should
                            // collapse into the hamburger menu on mobile/tablet. The
                            // Menu toggle right next to this can change it any time.
                            const link = e.target.value.trim();
                            if (
                                link &&
                                primarySelection.sectionId === HEADER_SECTION_ID &&
                                (selectedKind === "text" || selectedKind === "button") &&
                                selectedElement.menuItem === undefined
                            ) {
                                askConfirm(
                                    "Aa link ne mobile/tablet ma hamburger (☰) menu ma batavvi chhe?\n\nOK = menu ma batavo · Cancel = normal position par j rakho",
                                    () => applyToSelection({ menuItem: true }),
                                    { onCancel: () => applyToSelection({ menuItem: false }), okLabel: "Menu ma batavo", cancelLabel: "Normal rakho" }
                                );
                            }
                        }}
                        style={{ width: "200px", borderRadius: "4px", border: "none", padding: "4px 8px" }}
                    />
                    {primarySelection.sectionId === HEADER_SECTION_ID && (selectedKind === "text" || selectedKind === "button") && (
                        <>
                            <span className="text-sm text-white" title="Mobile/tablet par aa item hamburger menu ma jase (link jaruri)">☰ Menu</span>
                            <button
                                type="button"
                                onClick={() => {
                                    if (!selectedElement.link && !selectedElement.menuItem) {
                                        showToast({ severity: "warn", summary: "Link Required", detail: "Menu ma batavva mate pahela Link nakho.", life: 3000 });
                                        return;
                                    }
                                    applyToSelection( { menuItem: !selectedElement.menuItem });
                                }}
                                style={toolbarBtn(!!selectedElement.menuItem)}
                            >
                                {selectedElement.menuItem ? "ON" : "OFF"}
                            </button>
                        </>
                    )}
                    {multiSelected.length > 1 && (
                        <span className="text-sm text-white">{multiSelected.length} selected</span>
                    )}
                    <button
                        type="button"
                        onClick={removeSelection}
                        style={{ padding: "4px 10px", borderRadius: "4px", border: "none", cursor: "pointer", background: "#dc2626", color: "#fff" }}
                    >
                        <TrashIcon className="w-5 h-5" />
                    </button>
                </>
            )}
            <span className="text-xs" style={{ color: "#9ca3af" }}>
                {multiSelected.length > 1 ? "Ctrl/Cmd+click to add or remove · edits apply to all selected" : "Drag to move · click selected text to edit"}
            </span>
        </div>
    );

    // ---------- section controls ----------

    // Free Style (grows to fit content, no clip/scroll) only makes sense for
    // "content" sections — livevideo/slider have fixed-aspect content (a video
    // box, banner slides) with no risk of text overflow, so they keep the
    // plain Half/Full/custom-px picker they always had.
    const heightControls = (sec: Section, allowScreenModes = true) => {
        const freeStyleCapable = sec.type === "content";
        const isFreeStyle = freeStyleCapable && sec.heightMode === "auto";
        return (
            <>
                <span className="text-xs" style={{ color: "#4b5563" }}>Size</span>
                {freeStyleCapable && (
                    <>
                        <button
                            type="button"
                            onClick={() => updateSection(sec.id, { heightMode: "auto" })}
                            style={{
                                padding: "3px 10px", borderRadius: "4px", cursor: "pointer", fontSize: "12px",
                                border: "1px solid #264164",
                                background: isFreeStyle ? "#264164" : "#fff",
                                color: isFreeStyle ? "#fff" : "#264164",
                            }}
                        >
                            Free Style
                        </button>
                        <button
                            type="button"
                            onClick={() => updateSection(sec.id, { heightMode: "custom" })}
                            style={{
                                padding: "3px 10px", borderRadius: "4px", cursor: "pointer", fontSize: "12px",
                                border: "1px solid #264164",
                                background: !isFreeStyle ? "#264164" : "#fff",
                                color: !isFreeStyle ? "#fff" : "#264164",
                            }}
                        >
                            Manual
                        </button>
                    </>
                )}
                {!isFreeStyle && (
                    <>
                        {allowScreenModes && (
                            <>
                                <button
                                    type="button"
                                    onClick={() => updateSection(sec.id, { heightMode: "half" })}
                                    style={{
                                        padding: "3px 10px", borderRadius: "4px", cursor: "pointer", fontSize: "12px",
                                        border: "1px solid #264164",
                                        background: sec.heightMode === "half" ? "#264164" : "#fff",
                                        color: sec.heightMode === "half" ? "#fff" : "#264164",
                                    }}
                                >
                                    Half
                                </button>
                                <button
                                    type="button"
                                    onClick={() => updateSection(sec.id, { heightMode: "full" })}
                                    style={{
                                        padding: "3px 10px", borderRadius: "4px", cursor: "pointer", fontSize: "12px",
                                        border: "1px solid #264164",
                                        background: sec.heightMode === "full" ? "#264164" : "#fff",
                                        color: sec.heightMode === "full" ? "#fff" : "#264164",
                                    }}
                                >
                                    Full
                                </button>
                            </>
                        )}
                        <NumberInput
                            value={sec.minHeight}
                            min={60}
                            max={900}
                            step={10}
                            width={74}
                            title="Custom height (px)"
                            onFocusExtra={() => updateSection(sec.id, { heightMode: "custom" })}
                            onChange={(v) => updateSection(sec.id, { heightMode: "custom", minHeight: v })}
                            extraStyle={{ border: sec.heightMode === "custom" || !sec.heightMode ? "1px solid #264164" : "1px solid #e5e7eb" }}
                        />
                    </>
                )}
            </>
        );
    };

    const fixedToggle = (active: boolean, onClick: () => void) => (
        <button
            type="button"
            onClick={onClick}
            title="Background stays fixed while the page scrolls"
            style={{
                padding: "3px 10px", borderRadius: "4px", cursor: "pointer", fontSize: "12px",
                border: "1px solid #264164",
                background: active ? "#264164" : "#fff",
                color: active ? "#fff" : "#264164",
            }}
        >
            Fixed Bg
        </button>
    );

    const smallInput: React.CSSProperties = { borderRadius: "4px", border: "1px solid #e5e7eb", padding: "3px 8px", fontSize: "13px" };

    // Light-background toggle button — for controls sitting in the section's
    // white header row, as opposed to toolbarBtn's dark floating-toolbar look.
    const toolbarBtnLight = (active: boolean): React.CSSProperties => ({
        fontSize: "12px",
        padding: "3px 10px",
        borderRadius: "4px",
        cursor: "pointer",
        border: "1px solid #264164",
        background: active ? "#264164" : "#fff",
        color: active ? "#fff" : "#264164",
    });

    const elementButtons = (sec: Section) => (
        <>
            <Button label="Text" text icon={<PlusIcon className="w-5 h-5 mr-1" />} onClick={() => addElementToSection(sec, newTextElement(sec.type === "slider" || sec.id === FOOTER_SECTION_ID))} />
            <Button label="Icon" text icon={<StarIcon className="w-5 h-5 mr-1" />} onClick={() => addElementToSection(sec, newIconElement(sec.type === "slider" || sec.id === FOOTER_SECTION_ID))} />
            <Button label="Image" text icon={<PhotoIcon className="w-5 h-5 mr-1" />} loading={uploadingFor === sec.id} onClick={() => openImagePicker("imageel", sec)} />
            <Button label="Shape" text icon={<StopIcon className="w-5 h-5 mr-1" />} onClick={() => addElementToSection(sec, newBoxElement(sec.type === "slider"))} />
            <Button label="Button" text icon={<CursorArrowRaysIcon className="w-5 h-5 mr-1" />} onClick={() => addElementToSection(sec, newButtonElement())} />
        </>
    );

    // renders one editable section block (also used for the pinned header/footer canvases)
    const renderSectionEditor = (sec: Section, opts: { index?: number; pinned?: "header" | "footer" }) => {
        const { index, pinned } = opts;
        const slideIdx = Math.min(activeSlide[sec.id] || 0, Math.max(0, sec.slides.length - 1));
        const slide = sec.type === "slider" ? sec.slides[slideIdx] : null;
        // Full-bleed banner slide (same heuristic as the live SlideView): the
        // canvas grows to the banner's natural height instead of cropping it
        // at the section's fixed height — editor shows the whole image, same
        // as the live site/preview.
        const bannerImage = slide?.elements.find((el) => el.kind === "image" && (el.width || 0) >= 95 && (el.y || 0) === 0) || null;
        const isVideoSection = sec.type === "livevideo" || sec.type === "pagevideo" || sec.type === "event";
        const hasImageBg = sec.type === "slider" ? !!slide && !slide.bgColor : !!sec.bgUrl;
        const label = pinned === "header"
            ? "Header"
            : pinned === "footer"
                ? "Footer"
                : `${sec.type === "slider" ? "Slider" : sec.type === "content" ? "Text Section" : sec.type === "livevideo" ? "Live Videos" : sec.type === "pagevideo" ? "Page Videos" : "Events"} ${(index ?? 0) + 1}`;

        return (
            <div key={sec.id} className="border rounded-lg" style={{ borderColor: pinned ? "#c7d2fe" : "#e5e7eb", background: "#ffffff" }}>
                <div className="flex items-center justify-between flex-wrap gap-2 p-2" style={{ borderBottom: "1px solid #e5e7eb", background: pinned ? "#eef2ff" : undefined }}>
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold" style={{ color: "#264164" }}>{label}</span>
                        {pinned && (
                            <span className="text-xs" style={{ color: "#6b7280" }}>
                                add elements to replace the default {pinned}
                            </span>
                        )}
                        {pinned === "header" && (
                            <>
                                <span className="text-xs" title="Header page scroll thay tyare pan top par chipki rahe" style={{ color: "#4b5563" }}>Sticky</span>
                                <button
                                    type="button"
                                    onClick={() => setHeaderCfg((prev: any) => ({ ...prev, sticky: prev.sticky === false }))}
                                    style={toolbarBtnLight(headerCfg.sticky !== false)}
                                >
                                    {headerCfg.sticky !== false ? "ON" : "OFF"}
                                </button>
                            </>
                        )}
                        {sec.type === "livevideo" && (
                            <>
                                <span className="text-xs" style={{ color: "#4b5563" }}>Title</span>
                                <input
                                    type="text"
                                    value={sec.title || ""}
                                    placeholder="LIVE VIDEO"
                                    onChange={(e) => updateSection(sec.id, { title: e.target.value })}
                                    style={{ ...smallInput, width: "180px" }}
                                />
                            </>
                        )}
                        {(sec.type === "pagevideo" || sec.type === "event") && (
                            <>
                                {/* Same Bg Color control the "content" section type already
                                    has — the heading zone uses this instead of a hardcoded
                                    color, so it stays consistent with however the admin
                                    styles backgrounds everywhere else in the builder. */}
                                <span className="text-xs" style={{ color: "#4b5563" }}>Bg Color</span>
                                <input
                                    type="color"
                                    value={sec.bg === "transparent" ? "#ffffff" : sec.bg}
                                    onChange={(e) => updateSection(sec.id, { bg: e.target.value })}
                                    style={{ width: "38px", height: "28px", border: "none", background: "transparent", cursor: "pointer" }}
                                />
                                <button
                                    type="button"
                                    title="Section no potano bg color hatavi de — Page Bg (color/image) tena thi dekhay"
                                    onClick={() => updateSection(sec.id, { bg: sec.bg === "transparent" ? "#ffffff" : "transparent" })}
                                    style={toolbarBtnLight(sec.bg === "transparent")}
                                >
                                    Transparent
                                </button>
                                <span className="text-xs" style={{ color: "#4b5563" }}>Layout</span>
                                <button
                                    type="button"
                                    onClick={() => updateSection(sec.id, { displaymode: "grid" })}
                                    style={toolbarBtnLight((sec.displaymode || "grid") === "grid")}
                                >
                                    Grid
                                </button>
                                <button
                                    type="button"
                                    onClick={() => updateSection(sec.id, { displaymode: "slider" })}
                                    style={toolbarBtnLight(sec.displaymode === "slider")}
                                >
                                    Slider
                                </button>
                                {sec.displaymode === "slider" && (
                                    <>
                                        <span className="text-xs" style={{ color: "#4b5563" }}>Per Slide</span>
                                        {[1, 2, 3, 4].map((n) => (
                                            <button
                                                key={n}
                                                type="button"
                                                onClick={() => updateSection(sec.id, { slidecols: n })}
                                                style={toolbarBtnLight((sec.slidecols || 3) === n)}
                                            >
                                                {n}
                                            </button>
                                        ))}
                                    </>
                                )}
                                <Button
                                    label="Manage"
                                    text
                                    icon={<ListBulletIcon className="w-5 h-5 mr-1" />}
                                    onClick={() => setManageFor({ sectionId: sec.id, kind: sec.type as "event" | "pagevideo" })}
                                />
                            </>
                        )}
                        {/* Per-card styling for Page Videos/Events: bg color, and the
                            card's title (video's auto label, or the event's name) can
                            be hidden or recolored — same kind of controls text elements
                            already get, just scoped to the card instead of a draggable
                            element since the cards themselves are generated, not
                            individually placed. */}
                        {(sec.type === "pagevideo" || sec.type === "event") && (() => {
                            const titleVisible = sec.showcardtitle !== false;
                            return (
                                <>
                                    <span className="text-xs" style={{ color: "#4b5563" }}>Card Bg</span>
                                    <input
                                        type="color"
                                        value={sec.cardbg || "#f9fafb"}
                                        onChange={(e) => updateSection(sec.id, { cardbg: e.target.value })}
                                        style={{ width: "38px", height: "28px", border: "none", background: "transparent", cursor: "pointer" }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => updateSection(sec.id, { showcardtitle: !titleVisible })}
                                        style={toolbarBtnLight(titleVisible)}
                                    >
                                        {titleVisible ? "Hide Title" : "Show Title"}
                                    </button>
                                    {titleVisible && (
                                        <>
                                            <span className="text-xs" style={{ color: "#4b5563" }}>Title Color</span>
                                            <input
                                                type="color"
                                                value={sec.cardtitlecolor || "#111827"}
                                                onChange={(e) => updateSection(sec.id, { cardtitlecolor: e.target.value })}
                                                style={{ width: "38px", height: "28px", border: "none", background: "transparent", cursor: "pointer" }}
                                            />
                                        </>
                                    )}
                                    <span className="text-xs italic" style={{ color: "#9ca3af", flexBasis: "100%" }}>
                                        Note: these cards are auto-generated from your {sec.type === "pagevideo" ? "Page Videos" : "Events"} list, so unlike text/image/icon/shape/button elements they can't be dragged or repositioned individually — you can still change their background color and show/hide the title above.
                                    </span>
                                </>
                            );
                        })()}
                        {/* Title/subtitle are now real draggable text elements (see
                            defaultTitleSubtitleEls) — pagevideo/event get the same
                            +Text/Icon/Image/Shape/Button row as every other section
                            so the admin can add, move, restyle, or delete them. */}
                        {(!isVideoSection || sec.type === "pagevideo" || sec.type === "event") && elementButtons(sec)}
                        {/* Full-width lead image above this section's own content —
                            separate from Bg Image (which paints BEHIND content, not
                            as a block in the flow). Fades/slides in on scroll on the
                            live site (see BuilderSections.tsx). */}
                        {!pinned && (
                            <>
                                <Button
                                    label={sec.coverImageUrl ? "Change Cover Image" : "Cover Image"}
                                    text
                                    icon={<PhotoIcon className="w-5 h-5 mr-1" />}
                                    loading={uploadingFor === sec.id}
                                    onClick={() => openCoverImagePicker(sec)}
                                />
                                {sec.coverImageUrl && (
                                    <>
                                        <button
                                            type="button"
                                            title="Aa section pehla cover image batavvi ke nahi"
                                            onClick={() => updateSection(sec.id, { coverImageEnabled: sec.coverImageEnabled === false })}
                                            style={toolbarBtnLight(sec.coverImageEnabled !== false)}
                                        >
                                            {sec.coverImageEnabled !== false ? "ON" : "OFF"}
                                        </button>
                                        <select
                                            value={sec.coverImageAnimation || "slide"}
                                            onChange={(e) => updateSection(sec.id, { coverImageAnimation: e.target.value as Section["coverImageAnimation"] })}
                                            style={{ borderRadius: "4px", border: "1px solid #d1d5db", padding: "4px 6px", fontSize: "13px" }}
                                        >
                                            {ANIMATION_OPTIONS.map((opt) => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                        <Button label="Remove Cover Image" text style={{ color: "#dc2626" }} onClick={() => updateSection(sec.id, { coverImageUrl: "", coverImageEnabled: false })} />
                                    </>
                                )}
                            </>
                        )}
                        {!pinned && (() => {
                            const hasCoverImage = !!sec.coverImageUrl && sec.coverImageEnabled !== false;
                            return (
                                <>
                                    {hasCoverImage && (
                                        <>
                                            {/* Fully independent of Section Animation below — its own
                                                field (contentDirection), never reads or writes
                                                sec.sectionAnimation, so picking a value here never
                                                changes what that dropdown shows and vice versa. */}
                                            <span className="text-xs" title="Cover Image ni upar thi content aa direction ma scroll sathe slide thai ne aave" style={{ color: "#4b5563" }}>Scroll Direction</span>
                                            <select
                                                value={sec.contentDirection || "none"}
                                                onChange={(e) => updateSection(sec.id, { contentDirection: e.target.value as Section["contentDirection"] })}
                                                style={{ borderRadius: "4px", border: "1px solid #d1d5db", padding: "4px 6px", fontSize: "13px" }}
                                            >
                                                <option value="none">None</option>
                                                <option value="slide-down">Top to Bottom</option>
                                                <option value="slide">Bottom to Top</option>
                                                <option value="slide-right">Left to Right</option>
                                                <option value="slide-left">Right to Left</option>
                                            </select>
                                        </>
                                    )}
                                    <span className="text-xs" title="Aakhu section (background + content) scroll ma view ma aave tyare fade/slide/zoom thai ne dekhay" style={{ color: "#4b5563" }}>Section Animation</span>
                                    <select
                                        value={sec.sectionAnimation || "none"}
                                        onChange={(e) => updateSection(sec.id, { sectionAnimation: e.target.value as Section["sectionAnimation"] })}
                                        style={{ borderRadius: "4px", border: "1px solid #d1d5db", padding: "4px 6px", fontSize: "13px" }}
                                    >
                                        {ANIMATION_OPTIONS.map((opt) => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                    {!hasCoverImage && sec.sectionAnimation && sec.sectionAnimation !== "none" && (
                                        <>
                                            <input
                                                type="number"
                                                min={0.1}
                                                max={5}
                                                step={0.1}
                                                title="Animation ketli seconds ma pura thay"
                                                value={sec.animationDuration ?? 0.7}
                                                onChange={(e) => updateSection(sec.id, { animationDuration: parseFloat(e.target.value) || 0.7 })}
                                                style={{ width: "56px", borderRadius: "4px", border: "1px solid #d1d5db", padding: "4px 6px", fontSize: "13px" }}
                                            />
                                            <span className="text-xs" style={{ color: "#9ca3af" }}>sec</span>
                                        </>
                                    )}
                                </>
                            );
                        })()}
                        {sec.type === "slider" && slide && (
                            <>
                                <Button icon={<ChevronLeftIcon className="w-5 h-5" />} text onClick={() => changeSlide(sec, -1)} disabled={sec.slides.length < 2} />
                                <span className="text-xs" style={{ color: "#4b5563" }}>Slide {slideIdx + 1}/{sec.slides.length}</span>
                                <Button icon={<ChevronRightIcon className="w-5 h-5" />} text onClick={() => changeSlide(sec, 1)} disabled={sec.slides.length < 2} />
                                <Button label="Add Slide" text icon={<PlusIcon className="w-5 h-5 mr-1" />} onClick={() => addSlide(sec)} />
                                <Button label="Remove Slide" text style={{ color: "#dc2626" }} onClick={() => removeSlide(sec)} disabled={sec.slides.length < 2} />
                                <Button label="Background" text icon={<ArrowUpTrayIcon className="w-5 h-5 mr-1" />} loading={uploadingFor === sec.id} onClick={() => openBgPicker(sec)} />
                                <span className="text-xs" style={{ color: "#4b5563" }}>Bg Color</span>
                                <input
                                    type="color"
                                    value={slide.bgColor || "#111827"}
                                    onChange={(e) => setSlideBgColor(sec, e.target.value)}
                                    style={{ width: "38px", height: "28px", border: "none", background: "transparent", cursor: "pointer" }}
                                />
                                {slide.bgColor && (
                                    <Button label="Use Image" text onClick={() => setSlideBgColor(sec, "")} />
                                )}
                                {hasImageBg && fixedToggle(!!slide.bgFixed, () => toggleSlideBgFixed(sec))}
                            </>
                        )}
                        {sec.type === "content" && (
                            <>
                                <span className="text-xs" style={{ color: "#4b5563" }}>Bg Color</span>
                                <input
                                    type="color"
                                    value={sec.bg === "transparent" ? "#ffffff" : sec.bg}
                                    onChange={(e) => updateSection(sec.id, { bg: e.target.value })}
                                    style={{ width: "38px", height: "28px", border: "none", background: "transparent", cursor: "pointer" }}
                                />
                                <button
                                    type="button"
                                    title="Section no potano bg color hatavi de — Page Bg (color/image) tena thi dekhay"
                                    onClick={() => updateSection(sec.id, { bg: sec.bg === "transparent" ? "#ffffff" : "transparent" })}
                                    style={toolbarBtnLight(sec.bg === "transparent")}
                                >
                                    Transparent
                                </button>
                                <Button label="Bg Image" text icon={<ArrowUpTrayIcon className="w-5 h-5 mr-1" />} loading={uploadingFor === sec.id} onClick={() => openBgPicker(sec)} />
                                {sec.bgUrl && (
                                    <>
                                        <Button label="Remove Bg" text style={{ color: "#dc2626" }} onClick={() => updateSection(sec.id, { bgUrl: "", bgFixed: false })} />
                                        {fixedToggle(!!sec.bgFixed, () => updateSection(sec.id, { bgFixed: !sec.bgFixed }))}
                                    </>
                                )}
                            </>
                        )}
                        {(!isVideoSection || sec.type === "livevideo") && heightControls(sec, !pinned)}
                    </div>
                    {!pinned && index !== undefined && (
                        <div className="flex items-center gap-1">
                            <Button icon={<ArrowUpIcon className="w-5 h-5" />} text onClick={() => moveSection(index, -1)} disabled={index === 0} />
                            <Button icon={<ArrowDownIcon className="w-5 h-5" />} text onClick={() => moveSection(index, 1)} disabled={index === sections.length - 1} />
                            <Button icon={<TrashIcon className="w-5 h-5" />} text style={{ color: "#dc2626" }} onClick={() => removeSection(sec.id)} />
                        </div>
                    )}
                </div>

                {!pinned && sec.coverImageUrl && sec.coverImageEnabled !== false && (
                    <img
                        src={sec.coverImageUrl}
                        alt=""
                        className="w-full"
                        style={{ display: "block", maxHeight: "260px", objectFit: "cover" }}
                    />
                )}

                {(sec.type === "pagevideo" || sec.type === "event") ? (
                    <div className="relative" style={{ borderRadius: "0 0 8px 8px", overflow: "hidden", background: resolveSectionBg(sec.bg, pageBg) }}>
                        {/* Title/subtitle heading zone: same drag/select/resize/edit
                            interaction as any other section's text — 150px MUST match
                            the live renderer's own heading-zone height (BuilderSections.tsx)
                            or a position dragged here would read differently live.
                            Background lives on the wrapper above (see resolveSectionBg)
                            so it covers the whole section, matching the live site. */}
                        <div
                            // maxWidth+margin: caps the canvas at the same 1440px design
                            // width Preview/live site render at (via ResponsiveScaler) —
                            // without this, x% positions here read against whatever the
                            // admin's actual browser window happens to be, which drifted
                            // from Preview/live on anything wider than 1440px.
                            className="hc-canvas relative w-full"
                            style={{ height: "150px", maxWidth: "1440px", margin: "0 auto" }}
                            onMouseDown={(e) => onCanvasBackgroundMouseDown(e, sec.id, null)}
                        >
                            {sec.elements.map((el) => renderElement(sec, null, el))}
                            {renderMarqueeOverlay(sec.id, null)}
                        </div>
                        {sec.type === "pagevideo" ? (() => {
                            const visible = pageVideos.filter((v) => !(sec.excludedids || []).includes(v._id));
                            return visible.length > 0 ? (
                                <PageVideosSection
                                    videos={visible}
                                    displayMode={sec.displaymode}
                                    slideCols={sec.slidecols}
                                    cardBg={sec.cardbg}
                                    showCardTitle={sec.showcardtitle !== false}
                                    cardTitleColor={sec.cardtitlecolor}
                                />
                            ) : (
                                <div className="flex flex-col items-center justify-center gap-2 py-10" style={{ background: "#111827" }}>
                                    <VideoCameraIcon className="w-10 h-10" style={{ color: "#22c55e" }} />
                                    <span className="text-sm text-white">
                                        {pageVideos.length > 0 ? "All page videos are hidden from this section — use Manage to show some." : "No active page videos — add them in Settings → Page Videos"}
                                    </span>
                                </div>
                            );
                        })() : (() => {
                            const visible = upcomingEventsData.filter((ev) => !(sec.excludedids || []).includes(ev._id));
                            return visible.length > 0 ? (
                                <EventsSection
                                    events={visible}
                                    displayMode={sec.displaymode}
                                    slideCols={sec.slidecols}
                                    cardBg={sec.cardbg}
                                    showCardTitle={sec.showcardtitle !== false}
                                    cardTitleColor={sec.cardtitlecolor}
                                />
                            ) : (
                                <div className="flex flex-col items-center justify-center gap-2 py-10" style={{ background: "#111827" }}>
                                    <CalendarDaysIcon className="w-10 h-10" style={{ color: "#22c55e" }} />
                                    <span className="text-sm text-white">
                                        {upcomingEventsData.length > 0 ? "All events are hidden from this section — use Manage to show some." : "No upcoming events — add them in Settings → Events"}
                                    </span>
                                </div>
                            );
                        })()}
                        {/* On the outer wrapper (not the 150px heading zone above) so
                            the grid covers the whole section, including the video/
                            event card grid below the title. */}
                        {showGrid && <AlignmentGrid />}
                    </div>
                ) : (
                    <div
                        // Free Style: measured to fit its actual content (see the
                        // autoHeights effect) instead of a fixed px height — a fixed
                        // height with visible overflow does NOT push the next section
                        // down (only the box's real height affects normal document
                        // flow), so overflowing text used to visually collide with
                        // whatever section came after it. autoHeights keeps this in
                        // sync with the live site/Preview, which sizes the same way.
                        data-section-id={sec.id}
                        data-height-mode={sec.heightMode === "auto" ? "auto" : undefined}
                        className={`hc-canvas relative w-full${sec.heightMode === "auto" ? "" : " overflow-hidden"}`}
                        style={{
                            height: bannerImage
                                ? undefined
                                : (sec.heightMode === "auto"
                                    ? `${Math.max(sec.minHeight || 300, autoHeights[sec.id] || 0)}px`
                                    : sectionHeight(sec)),
                            background: sec.type === "content" ? sec.bg : "#000",
                            // Same 1440px cap as the pagevideo/event heading zone above —
                            // matches the design width Preview/live site render at via
                            // ResponsiveScaler, instead of the admin's raw browser width.
                            maxWidth: "1440px",
                            margin: "0 auto",
                        }}
                        onMouseDown={(e) => onCanvasBackgroundMouseDown(e, sec.id, slide ? slide.id : null)}
                    >
                        {/* invisible in-flow sizer: gives the canvas the banner's
                            natural height; the interactive copy paints on top via
                            renderElement at the exact same size */}
                        {bannerImage?.url && (
                            <img
                                src={bannerImage.url}
                                alt=""
                                style={{ width: `${bannerImage.width || 100}%`, height: "auto", display: "block", visibility: "hidden" }}
                            />
                        )}
                        {sec.type === "livevideo" && (
                            <div className="absolute top-0 left-0 right-0 text-center px-4 pt-6" style={{ pointerEvents: "none" }}>
                                <SplitHeading title={sec.title || "LIVE VIDEO"} light />
                            </div>
                        )}
                        {sec.type === "livevideo" && sec.elements.map((el) => renderElement(sec, null, el))}
                        {sec.type === "slider" && slide && (
                            <>
                                {slide.bgColor ? (
                                    <div className="absolute top-0 left-0 w-full h-full" style={{ background: slide.bgColor, pointerEvents: "none" }}></div>
                                ) : (
                                    <div className="absolute top-0 left-0 w-full h-full" style={{ pointerEvents: "none" }}>
                                        <SectionBgImage
                                            url={slide.bgUrl || backgrounds[slide.bgIndex % backgrounds.length]}
                                            fixed={slide.bgFixed}
                                            overlay
                                        />
                                    </div>
                                )}
                                {slide.elements.map((el) => renderElement(sec, slide.id, el))}
                            </>
                        )}
                        {sec.type === "content" && (
                            <>
                                {sec.bgUrl && (
                                    <div className="absolute top-0 left-0 w-full h-full" style={{ pointerEvents: "none" }}>
                                        <SectionBgImage url={sec.bgUrl} fixed={sec.bgFixed} />
                                    </div>
                                )}
                                {(() => {
                                    // A caption positioned at its own fixed y% only lines up
                                    // under the photo by coincidence — both are really percentages
                                    // of the WHOLE section, which now correctly grows to fit
                                    // whatever the tallest content needs (see autoHeights above).
                                    // When that stretches the section taller, an independently
                                    // positioned caption drifts away from the photo instead of
                                    // staying under it. Detect text sitting directly under an
                                    // image (same x, greater y) and render them as one group —
                                    // positioned by the image's own y only, caption stacked right
                                    // below it via margin — matching the live site/Preview
                                    // (BuilderSections.tsx), which already does this.
                                    //
                                    // Header/footer chrome never runs this heuristic — a header's
                                    // logo+title (image, then text beside/near it) matches the
                                    // exact same geometric pattern as a photo+caption and would
                                    // get incorrectly forced into a stacked image-then-caption
                                    // layout otherwise (see BuilderSections.tsx's same exclusion).
                                    if (pinned === "header" || pinned === "footer") {
                                        return sec.elements.map((el) => renderElement(sec, null, el));
                                    }
                                    const allElements = sec.elements;
                                    const usedIds = new Set<string>();
                                    const imageCaptionGroups: { image: BuilderEl; captions: BuilderEl[] }[] = [];
                                    allElements
                                        .filter((el) => el.kind === "image")
                                        .forEach((img) => {
                                            const captions = allElements
                                                .filter((el) => el.kind === "text" && !usedIds.has(el.id) && Math.abs((img.x || 0) - (el.x || 0)) <= 8 && (el.y || 0) > (img.y || 0))
                                                .sort((a, b) => (a.y || 0) - (b.y || 0));
                                            if (captions.length) {
                                                captions.forEach((c) => usedIds.add(c.id));
                                                usedIds.add(img.id);
                                                imageCaptionGroups.push({ image: img, captions });
                                            }
                                        });
                                    const ungroupedElements = allElements.filter((el) => !usedIds.has(el.id));
                                    return (
                                        <>
                                            {imageCaptionGroups.map(({ image, captions }) => (
                                                <div
                                                    key={image.id}
                                                    data-el-item
                                                    data-el-y={image.y || 0}
                                                    style={{ position: "absolute", left: `${image.x || 0}%`, top: `${image.y || 0}%`, display: "flex", flexDirection: "column", alignItems: "flex-start" }}
                                                >
                                                    {renderElement(sec, null, image, { staticFlow: true })}
                                                    {captions.map((c) => (
                                                        <div key={c.id} style={{ marginTop: "8px" }}>
                                                            {renderElement(sec, null, c, { staticFlow: true })}
                                                        </div>
                                                    ))}
                                                </div>
                                            ))}
                                            {ungroupedElements.map((el) => renderElement(sec, null, el))}
                                        </>
                                    );
                                })()}
                                {pinned === "header" && sec.elements.length === 0 && (
                                    <div className="absolute top-0 left-0 w-full h-full overflow-hidden" style={{ pointerEvents: "none" }}>
                                        <SiteHeaderBar header={headerCfg} logo={logosmall} />
                                    </div>
                                )}
                                {pinned === "footer" && sec.elements.length === 0 && (
                                    <div className="absolute top-0 left-0 w-full h-full overflow-hidden" style={{ pointerEvents: "none" }}>
                                        <SiteFooterBar footer={footerCfg}>
                                            <div className="flex items-center gap-1">
                                                <span style={{ color: "#a78bfa" }}>Privacy</span>
                                                <span>/</span>
                                                <span style={{ color: "#a78bfa" }}>Terms</span>
                                            </div>
                                        </SiteFooterBar>
                                    </div>
                                )}
                            </>
                        )}
                        {showGrid && <AlignmentGrid />}
                        {renderMarqueeOverlay(sec.id, slide ? slide.id : null)}
                    </div>
                )}
            </div>
        );
    };

    const headerCustomActive = headerSec.elements.length > 0;
    const footerCustomActive = footerSec.elements.length > 0;

    if (isSmallScreen) {
        return (
            <div className="standalone-page-bg">
                <div className="back-button" onClick={handleBack}>
                    <ArrowLeftIcon className="w-6 h-6 text-black-700" /><h3 className="m-0">Back</h3>
                </div>
                <div
                    className="flex flex-col items-center justify-center text-center mx-4"
                    style={{ minHeight: "70vh" }}
                >
                    <ComputerDesktopIcon style={{ width: "56px", height: "56px", color: "#9ca3af", marginBottom: "16px" }} />
                    <h3 className="m-0 mb-2" style={{ color: "#264164" }}>Please use a laptop or desktop</h3>
                    <p className="m-0" style={{ color: "#6b7280", maxWidth: "360px" }}>
                        The Home Content builder needs a bigger screen to work properly. Please open this page on a laptop or desktop computer — it isn't available on mobile or tablet.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="standalone-page-bg">
            <div className="back-button" onClick={handleBack}>
                <ArrowLeftIcon className="w-6 h-6 text-black-700" /><h3 className="m-0">Back</h3>
            </div>
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleImageSelected}
            />
            <div className="table-margin">
                <div className="bg-white p-4 sm:p-6" style={{ borderRadius: "8px" }}>
                    {/* Page bar: Home + extra builder pages + add new */}
                    <div className="flex items-center gap-2 flex-wrap mb-3 pb-3" style={{ borderBottom: "1px solid #e5e7eb" }}>
                        <button
                            type="button"
                            onClick={() => switchToPage(null)}
                            className="px-3 py-1.5 rounded-lg text-sm font-semibold cursor-pointer"
                            style={activePageId === null
                                ? { background: "#264164", color: "#fff", border: "1px solid #264164" }
                                : { background: "#fff", color: "#264164", border: "1px solid #d1d5db" }}
                        >
                            Home
                        </button>
                        {sitePages.map((p) => (
                            <button
                                key={p._id}
                                type="button"
                                onClick={() => switchToPage(p._id)}
                                className="px-3 py-1.5 rounded-lg text-sm font-semibold cursor-pointer"
                                style={activePageId === p._id
                                    ? { background: "#264164", color: "#fff", border: "1px solid #264164" }
                                    : { background: "#fff", color: "#264164", border: "1px solid #d1d5db" }}
                            >
                                {p.title}
                            </button>
                        ))}
                        <button
                            type="button"
                            onClick={() => setShowAddPage(true)}
                            className="px-3 py-1.5 rounded-lg text-sm font-semibold cursor-pointer flex items-center gap-1"
                            style={{ background: "#fff", color: "#16a34a", border: "1px dashed #16a34a" }}
                        >
                            <PlusIcon className="w-4 h-4" /> Add Page
                        </button>
                        {activePage && (
                            <div className="flex items-center gap-2 ml-auto">
                                <a
                                    href={`/p/${activePage.slug}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-xs underline"
                                    style={{ color: "#2563eb" }}
                                >
                                    /p/{activePage.slug}
                                </a>
                                <button
                                    type="button"
                                    onClick={handleDeletePage}
                                    title="Delete this page"
                                    className="p-1.5 rounded-lg cursor-pointer flex items-center"
                                    style={{ background: "#fff", color: "#dc2626", border: "1px solid #fca5a5" }}
                                >
                                    <TrashIcon className="w-4 h-4" />
                                </button>
                            </div>
                        )}
                    </div>

                    {activePage && (
                        <div className="flex flex-col gap-2 mb-4 p-3" style={{ background: "#f9fafb", borderRadius: "8px", border: "1px solid #e5e7eb" }}>
                            <span className="text-xs font-semibold" style={{ color: "#4b5563" }}>
                                SEO — Google search results maate (blank rakho to page title/content use thase)
                            </span>
                            <div className="flex flex-col sm:flex-row gap-2">
                                <input
                                    type="text"
                                    value={metaTitle}
                                    placeholder={`Meta Title (e.g. ${activePage.title} | Sagar Samaj)`}
                                    onChange={(e) => setMetaTitle(e.target.value)}
                                    className="flex-1 border border-gray-300 rounded px-3 text-sm focus:outline-none focus:border-blue-500"
                                    style={{ height: "38px" }}
                                />
                                <input
                                    type="text"
                                    value={metaKeywords}
                                    placeholder="Meta Keywords (comma separated)"
                                    onChange={(e) => setMetaKeywords(e.target.value)}
                                    className="flex-1 border border-gray-300 rounded px-3 text-sm focus:outline-none focus:border-blue-500"
                                    style={{ height: "38px" }}
                                />
                            </div>
                            <textarea
                                value={metaDescription}
                                placeholder="Meta Description (150-160 characters recommended)"
                                onChange={(e) => setMetaDescription(e.target.value)}
                                rows={2}
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                                style={{ resize: "vertical" }}
                            />
                        </div>
                    )}

                    <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                        <h2 className="card-title m-0 text-lg sm:text-xl">{activePage ? `${activePage.title} Page` : "Home Content"}</h2>
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs" style={{ color: "#4b5563" }}>Page Bg</span>
                            <input
                                type="color"
                                value={pageBg || "#ffffff"}
                                onChange={(e) => setPageBg(e.target.value)}
                                style={{ width: "38px", height: "28px", border: "none", background: "transparent", cursor: "pointer" }}
                            />
                            <Button
                                label={pageBgImage ? "Change Bg Image" : "Bg Image"}
                                text
                                icon={<ArrowUpTrayIcon className="w-5 h-5 mr-1" />}
                                loading={uploadingFor === "__page__"}
                                onClick={openPageBgImagePicker}
                            />
                            {pageBgImage && (
                                <>
                                    <button
                                        type="button"
                                        title="Scroll thay tyare fixed rahe, sections tena upar thi slide thay"
                                        onClick={() => setPageBgImageFixed((prev) => !prev)}
                                        style={toolbarBtnLight(pageBgImageFixed)}
                                    >
                                        {pageBgImageFixed ? "Fixed" : "Scrolls"}
                                    </button>
                                    <Button label="Remove Bg Image" text style={{ color: "#dc2626" }} onClick={() => setPageBgImage("")} />
                                </>
                            )}
                            <span className="text-xs" title="App Install / Rate Us floating banner batavvu ke nahi — ON kariye to desktop par pan batashe" style={{ color: "#4b5563" }}>Install Banner</span>
                            <button
                                type="button"
                                onClick={() => setShowInstallBanner((prev) => !prev)}
                                style={toolbarBtnLight(showInstallBanner)}
                            >
                                {showInstallBanner ? "ON" : "OFF"}
                            </button>
                            <Button
                                label="Text Section"
                                outlined
                                icon={<PlusIcon className="w-5 h-5 mr-1" />}
                                style={{ color: "#264164", borderColor: "#264164" }}
                                disabled={loading}
                                onClick={() => setSections((prev) => [...prev, newContentSection()])}
                            />
                            <Button
                                label="About"
                                outlined
                                icon={<InformationCircleIcon className="w-5 h-5 mr-1" />}
                                style={{ color: "#264164", borderColor: "#264164" }}
                                disabled={loading}
                                onClick={() => setSections((prev) => [...prev, newAboutSection()])}
                            />
                            <Button
                                label="Slider"
                                outlined
                                icon={<Squares2X2Icon className="w-5 h-5 mr-1" />}
                                style={{ color: "#264164", borderColor: "#264164" }}
                                disabled={loading}
                                onClick={() => setSections((prev) => [...prev, newSliderSection()])}
                            />
                            <Button
                                label="Live Videos"
                                outlined
                                icon={<VideoCameraIcon className="w-5 h-5 mr-1" />}
                                style={{ color: "#264164", borderColor: "#264164" }}
                                disabled={loading || sections.some((s) => s.type === "livevideo")}
                                onClick={() => setSections((prev) => [...prev, newVideoSection("livevideo")])}
                            />
                            <Button
                                label="Page Videos"
                                outlined
                                icon={<VideoCameraIcon className="w-5 h-5 mr-1" />}
                                style={{ color: "#264164", borderColor: "#264164" }}
                                disabled={loading || sections.some((s) => s.type === "pagevideo")}
                                onClick={() => setSections((prev) => [...prev, newVideoSection("pagevideo")])}
                            />
                            <Button
                                label="Events"
                                outlined
                                icon={<CalendarDaysIcon className="w-5 h-5 mr-1" />}
                                style={{ color: "#264164", borderColor: "#264164" }}
                                disabled={loading || sections.some((s) => s.type === "event")}
                                onClick={() => setSections((prev) => [...prev, newEventSection()])}
                            />
                            <Button
                                label="Grid"
                                outlined={!showGrid}
                                icon={<TableCellsIcon className="w-5 h-5 mr-1" />}
                                style={{ color: showGrid ? "#fff" : "#264164", background: showGrid ? "#264164" : undefined, borderColor: "#264164" }}
                                onClick={() => setShowGrid((p) => !p)}
                            />
                            <Button
                                label="Preview"
                                severity="secondary"
                                icon={<EyeIcon className="w-5 h-5 mr-1" />}
                                disabled={loading}
                                onClick={() => {
                                    flushPendingTextEdit();
                                    setSelected(null);
                                    setEditingId(null);
                                    setPreviewPageId(activePageId);
                                    setPreviewCache({});
                                    setShowPreview(true);
                                }}
                            />
                            <Button label="Save" loading={saving} disabled={loading} onClick={handleSave} />
                        </div>
                    </div>

                    <div
                        className="flex flex-col gap-4"
                        style={pageBgImage ? {
                            backgroundImage: `url(${pageBgImage})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            backgroundAttachment: pageBgImageFixed ? "fixed" : undefined,
                            padding: "12px",
                            borderRadius: "8px",
                        } : undefined}
                    >
                        {loading ? (
                            Array.from({ length: 3 }, (_, i) => (
                                <div key={i} className="border rounded-lg" style={{ borderColor: "#e5e7eb" }}>
                                    <div className="flex items-center gap-2 p-2" style={{ borderBottom: "1px solid #e5e7eb" }}>
                                        <Skeleton width="120px" height="1rem" />
                                    </div>
                                    <Skeleton width="100%" height="220px" borderRadius="0 0 8px 8px" />
                                </div>
                            ))
                        ) : (
                            <>
                                {/* Header/footer are site-wide — edited (and saved) only on the Home tab */}
                                {activePageId === null ? (
                                    renderSectionEditor(headerSec, { pinned: "header" })
                                ) : (
                                    <p className="text-xs text-center py-2 m-0 rounded" style={{ color: "#6b7280", background: "#f9fafb", border: "1px dashed #e5e7eb" }}>
                                        Header ane Footer site-wide chhe — edit karva Home page tab par jao. Aa page live thase tyare header/footer automatic aavi jase.
                                    </p>
                                )}
                                {sections.map((sec, index) => renderSectionEditor(sec, { index }))}
                                {sections.length === 0 && (
                                    <p className="text-sm text-center py-8 m-0" style={{ color: "#6b7280" }}>
                                        No sections yet — add a Slider or Text Section above.
                                    </p>
                                )}
                                {activePageId === null && renderSectionEditor(footerSec, { pinned: "footer" })}
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Add new site page */}
            <Dialog
                header="Add New Page"
                visible={showAddPage}
                style={{ width: "92vw", maxWidth: "440px" }}
                modal
                blockScroll
                onHide={() => setShowAddPage(false)}
            >
                <div className="flex flex-col gap-3">
                    <div>
                        <label className="form-label block mb-1">Page Title</label>
                        <input
                            type="text"
                            value={newPageTitle}
                            autoFocus
                            placeholder="e.g. About Us"
                            onChange={(e) => {
                                setNewPageTitle(e.target.value);
                                setNewPageSlug(slugify(e.target.value));
                            }}
                            className="w-full border border-gray-300 rounded px-3 text-sm focus:outline-none focus:border-blue-500"
                            style={{ height: "42px" }}
                        />
                    </div>
                    <div>
                        <label className="form-label block mb-1">URL Slug</label>
                        <div className="flex items-center gap-1">
                            <span className="text-sm" style={{ color: "#6b7280" }}>/p/</span>
                            <input
                                type="text"
                                value={newPageSlug}
                                placeholder="about-us"
                                onChange={(e) => setNewPageSlug(slugify(e.target.value))}
                                className="flex-1 border border-gray-300 rounded px-3 text-sm focus:outline-none focus:border-blue-500"
                                style={{ height: "42px" }}
                            />
                        </div>
                        <p className="text-xs m-0 mt-1" style={{ color: "#9ca3af" }}>
                            Page live thase: sagarsamaj.in/p/{newPageSlug || "..."}
                        </p>
                    </div>
                    <div className="flex justify-end gap-2 mt-2">
                        <Button label="Cancel" outlined onClick={() => setShowAddPage(false)} />
                        <Button label="Create Page" loading={creatingPage} onClick={handleCreatePage} />
                    </div>
                </div>
            </Dialog>

            {/* Background template / uploaded image picker */}
            <Dialog
                header="Choose Background"
                visible={!!bgPicker}
                style={{ width: "92vw", maxWidth: "760px" }}
                modal
                draggable={false}
                blockScroll
                onHide={() => setBgPicker(null)}
            >
                <div className="flex flex-col gap-4">
                    {bgPicker?.allowTemplates && (
                        <div>
                            <h4 className="m-0 mb-2 text-sm font-semibold" style={{ color: "#264164" }}>Templates</h4>
                            <div className="grid grid-cols-3 gap-3">
                                {backgrounds.map((bg, i) => (
                                    <img
                                        key={i}
                                        src={bg}
                                        alt={`Template ${i + 1}`}
                                        className="w-full rounded-lg cursor-pointer"
                                        style={{ height: "110px", objectFit: "cover", border: "2px solid transparent" }}
                                        onClick={() => applyBgTemplate(i)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <h4 className="m-0 text-sm font-semibold" style={{ color: "#264164" }}>Uploaded Images</h4>
                            <Button
                                label="Upload New"
                                size="small"
                                icon={<ArrowUpTrayIcon className="w-4 h-4 mr-1" />}
                                onClick={uploadFromPicker}
                            />
                        </div>
                        {uploadedImages.length === 0 ? (
                            <p className="text-sm m-0" style={{ color: "#6b7280" }}>No uploaded images yet — click Upload New.</p>
                        ) : (
                            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" style={{ maxHeight: "320px", overflowY: "auto" }}>
                                {uploadedImages.map((img) => (
                                    <img
                                        key={img.url}
                                        src={img.url}
                                        alt={img.name}
                                        className="w-full rounded-lg cursor-pointer"
                                        style={{ height: "90px", objectFit: "cover" }}
                                        onClick={() => applyBgImage(img.url)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </Dialog>

            {/* Manage events / page videos shown in THIS section — hides an item
                from this home section only; the item itself is unaffected and
                still lives (and can be deleted) under Settings → Events / Page
                Videos, and still shows in any OTHER section that includes it. */}
            <Dialog
                header={manageFor?.kind === "event" ? "Manage Events in this Section" : "Manage Page Videos in this Section"}
                visible={!!manageFor}
                style={{ width: "92vw", maxWidth: "820px" }}
                modal
                draggable={false}
                blockScroll
                onHide={() => setManageFor(null)}
                // Own layout instead of PrimeReact's default content padding: a
                // fixed-height flex column with a scrollable middle, so "Done"
                // stays pinned at the bottom of the DIALOG regardless of how
                // many items there are, instead of trailing after the list.
                contentStyle={{ padding: 0, display: "flex", flexDirection: "column", maxHeight: "78vh" }}
            >
                {manageFor && (() => {
                    const sec = (manageFor.sectionId === HEADER_SECTION_ID ? headerSec : manageFor.sectionId === FOOTER_SECTION_ID ? footerSec : sections.find((s) => s.id === manageFor.sectionId));
                    const excluded = new Set(sec?.excludedids || []);
                    // Upcoming first — past events (greyed out, never actually shown)
                    // trail at the end instead of mixing in among live ones.
                    const items = manageFor.kind === "event"
                        ? [...eventsData].sort((a, b) => Number(isUpcomingEvent(b)) - Number(isUpcomingEvent(a)))
                        : pageVideos;
                    const toggleItem = (id: string) => {
                        const next = new Set(excluded);
                        if (next.has(id)) next.delete(id); else next.add(id);
                        updateSection(manageFor.sectionId, { excludedids: Array.from(next) });
                    };
                    return (
                        <>
                            <p className="text-sm m-0" style={{ color: "#6b7280", padding: "20px 24px 16px" }}>
                                Toggle off to HIDE an item from this section only — it stays untouched everywhere else.
                            </p>
                            <div style={{ flex: 1, overflowY: "auto", padding: "0 24px 20px" }}>
                                {items.length === 0 ? (
                                    <p className="text-sm m-0" style={{ color: "#6b7280" }}>
                                        Nothing here yet — add {manageFor.kind === "event" ? "events under Settings → Events" : "videos under Settings → Page Videos"}.
                                    </p>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 items-stretch gap-4">
                                        {items.map((item: any) => {
                                            const isHidden = excluded.has(item._id);
                                            // Past events never render in the actual section regardless
                                            // of this toggle (the section only ever asks for upcoming
                                            // ones) — show that plainly instead of a toggle that would
                                            // silently do nothing.
                                            const isPast = manageFor.kind === "event" && !isUpcomingEvent(item);
                                            return (
                                                <div
                                                    key={item._id}
                                                    // h-full (fills the grid row's stretched height, equal
                                                    // across every card regardless of its own content length)
                                                    // + the preview wrapper below being flex-1 is what pins
                                                    // every "Visible/Hidden" button to the same bottom edge.
                                                    className="h-full border rounded-lg p-3 flex flex-col gap-2"
                                                    style={{ borderColor: isPast || isHidden ? "#e5e7eb" : "#264164", opacity: isPast ? 0.45 : isHidden ? 0.55 : 1 }}
                                                >
                                                    {/* zoom (not transform:scale) shrinks the preview AND its
                                                        reserved layout space together — transform only shrinks
                                                        the paint, leaving a gap that doesn't match what's
                                                        visually there. flex-1 fills the card slot's full
                                                        height so every slot (across the whole grid, via
                                                        items-stretch above) ends up the same size. */}
                                                    <div className="flex-1 w-full" style={{ pointerEvents: "none", zoom: 0.92, filter: isPast ? "grayscale(1)" : undefined }}>
                                                        {manageFor.kind === "event" ? (
                                                            <EventCard ev={item} />
                                                        ) : (
                                                            <div className="bg-gray-50 rounded-lg p-2 w-full">
                                                                <h4 className="font-semibold m-0 mb-2 text-sm">{getSectionLabel(item.page)}</h4>
                                                                <VideoEmbed link={item.link} title={getSectionLabel(item.page)} />
                                                            </div>
                                                        )}
                                                    </div>
                                                    {isPast ? (
                                                        <span className="text-xs font-medium" style={{ color: "#9ca3af", alignSelf: "flex-start" }}>
                                                            Date passed — never shows here
                                                        </span>
                                                    ) : (
                                                        <Button
                                                            label={isHidden ? "Hidden — click to Show" : "Visible — click to Hide"}
                                                            text
                                                            size="small"
                                                            icon={isHidden ? <EyeSlashIcon className="w-4 h-4 mr-1" /> : <EyeIcon className="w-4 h-4 mr-1" />}
                                                            style={{ color: isHidden ? "#9ca3af" : "#16a34a", alignSelf: "flex-start" }}
                                                            onClick={() => toggleItem(item._id)}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                            <div className="flex justify-end" style={{ padding: "14px 24px", borderTop: "1px solid #e5e7eb" }}>
                                <Button label="Done" onClick={() => setManageFor(null)} />
                            </div>
                        </>
                    );
                })()}
            </Dialog>

            <Dialog
                header={`${previewPageName} Page Preview`}
                visible={showPreview}
                maximized
                modal
                draggable={false}
                blockScroll
                onHide={() => setShowPreview(false)}
                contentStyle={{ padding: 0 }}
            >
                <div
                    className="flex flex-col items-center gap-2 py-3"
                    style={{ borderBottom: "1px solid #e5e7eb", background: "#f9fafb", position: "sticky", top: 0, zIndex: 10 }}
                >
                    {/* Page selector — preview any page; the one being edited shows
                        its live (unsaved) state, others show their last-saved version */}
                    <div className="flex items-center justify-center gap-2 flex-wrap">
                        <button
                            type="button"
                            onClick={() => selectPreviewPage(null)}
                            className="px-3 py-1 rounded-lg text-sm font-semibold cursor-pointer"
                            style={previewPageId === null
                                ? { background: "#16a34a", color: "#fff", border: "1px solid #16a34a" }
                                : { background: "#fff", color: "#264164", border: "1px solid #d1d5db" }}
                        >
                            Home{activePageId === null ? "" : " (saved)"}
                        </button>
                        {sitePages.map((p) => (
                            <button
                                key={p._id}
                                type="button"
                                onClick={() => selectPreviewPage(p._id)}
                                className="px-3 py-1 rounded-lg text-sm font-semibold cursor-pointer"
                                style={previewPageId === p._id
                                    ? { background: "#16a34a", color: "#fff", border: "1px solid #16a34a" }
                                    : { background: "#fff", color: "#264164", border: "1px solid #d1d5db" }}
                            >
                                {p.title}{activePageId === p._id ? "" : " (saved)"}
                            </button>
                        ))}
                    </div>
                    <div className="flex items-center justify-center gap-2 flex-wrap">
                        {([
                            { mode: "mobile" as const, label: "Mobile", icon: DevicePhoneMobileIcon },
                            { mode: "tablet" as const, label: "Tablet", icon: DeviceTabletIcon },
                            { mode: "desktop" as const, label: "Desktop", icon: ComputerDesktopIcon },
                            { mode: "large" as const, label: "Large Desktop", icon: TvIcon },
                        ]).map(({ mode, label, icon: Icon }) => (
                            <button
                                key={mode}
                                type="button"
                                onClick={() => setPreviewMode(mode)}
                                style={{
                                    display: "flex", alignItems: "center", gap: "6px", padding: "6px 16px", borderRadius: "6px", cursor: "pointer", fontSize: "14px",
                                    border: "1px solid #264164",
                                    background: previewMode === mode ? "#264164" : "#fff",
                                    color: previewMode === mode ? "#fff" : "#264164",
                                }}
                            >
                                <Icon className="w-5 h-5" /> {label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* onClickCapture: internal links navigate the preview itself */}
                <div onClickCapture={handlePreviewLinkClick}>
                {previewFetching || !previewContent ? (
                    <div className="flex items-center justify-center" style={{ padding: "80px 0" }}>
                        <Skeleton width="60%" height="300px" />
                    </div>
                ) : previewMode === "desktop" ? (
                    // HomeContentView wraps its own ResponsiveScaler internally (same
                    // as the real site) — without it there's no scale context and no
                    // 1440px cap, so this mode silently rendered wider/narrower than
                    // the live site depending on the dialog's own width instead of
                    // matching it.
                    <HomeContentView
                        homeContent={{
                            sections: previewContent.sections,
                            pagebg: previewContent.pagebg,
                            pagebgimage: previewContent.pagebgimage,
                            pagebgimagefixed: previewContent.pagebgimagefixed,
                            showinstallbanner: previewContent.showinstallbanner,
                            header: { ...headerCfg, custom: headerCustomActive ? headerSec : null },
                            footer: { ...footerCfg, custom: footerCustomActive ? footerSec : null },
                        }}
                        liveVideos={liveVideos}
                        pageVideos={pageVideos}
                        events={upcomingEventsData}
                        logo={logosmall}
                        sticky={false}
                    />
                ) : previewMode === "large" ? (
                    // 1920px content never actually scales (the design is 1440px and ResponsiveScaler
                    // only ever shrinks, never enlarges — same as real life), but a fixed-width frame
                    // lets the admin see exactly how much side margin/whitespace shows up on a big monitor.
                    // forceWidth=1920 here is what actually makes ResponsiveScaler apply that
                    // 1440px-centered cap inside the frame — without it (as before) content just
                    // stretched to fill the full 1920px frame with no margins at all.
                    <div className="flex justify-center" style={{ background: "#e5e7eb", padding: "24px 0", overflowX: "auto" }}>
                        <div style={{ width: "1920px", border: "8px solid #1f2937", borderRadius: "10px", background: "#fff" }}>
                            <HomeContentView
                                homeContent={{
                                    sections: previewContent.sections,
                                    pagebg: previewContent.pagebg,
                                    pagebgimage: previewContent.pagebgimage,
                                    pagebgimagefixed: previewContent.pagebgimagefixed,
                                    showinstallbanner: previewContent.showinstallbanner,
                                    header: { ...headerCfg, custom: headerCustomActive ? headerSec : null },
                                    footer: { ...footerCfg, custom: footerCustomActive ? footerSec : null },
                                }}
                                liveVideos={liveVideos}
                                pageVideos={pageVideos}
                                events={upcomingEventsData}
                                logo={logosmall}
                                sticky={false}
                                forceWidth={1920}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="flex justify-center" style={{ background: "#e5e7eb", padding: "24px 0" }}>
                        <div
                            className="phone-frame-scroll"
                            style={
                                // contain:paint force-clips every descendant's painting to
                                // the frame box — counter-zoomed chrome (hamburger etc.)
                                // otherwise escapes the scroll clip in Chromium and floats
                                // outside the frame. (The drawer is unaffected: it renders
                                // through a body portal, intentionally outside the frame.)
                                previewMode === "tablet"
                                    ? { width: "784px", height: "min(1000px, 82vh)", overflowY: "auto", border: "10px solid #1f2937", borderRadius: "20px", background: "#fff", contain: "paint" }
                                    : { width: "406px", height: "min(800px, 82vh)", overflowY: "auto", border: "8px solid #1f2937", borderRadius: "28px", background: "#fff", contain: "paint" }
                            }
                        >
                            <HomeContentView
                                homeContent={{
                                    sections: previewContent.sections,
                                    pagebg: previewContent.pagebg,
                                    pagebgimage: previewContent.pagebgimage,
                                    pagebgimagefixed: previewContent.pagebgimagefixed,
                                    showinstallbanner: previewContent.showinstallbanner,
                                    header: { ...headerCfg, custom: headerCustomActive ? headerSec : null },
                                    footer: { ...footerCfg, custom: footerCustomActive ? footerSec : null },
                                }}
                                liveVideos={liveVideos}
                                pageVideos={pageVideos}
                                events={upcomingEventsData}
                                logo={logosmall}
                                sticky={false}
                                forceWidth={previewMode === "tablet" ? 768 : 390}
                            />
                        </div>
                    </div>
                )}
                </div>
            </Dialog>

            {primarySelection && toolbarPos && (
                <div ref={toolbarRef} style={{ position: "fixed", top: toolbarPos.top, left: toolbarPos.left, zIndex: 1100 }}>
                    {elementToolbar}
                </div>
            )}
        </div>
    );
};

export default HomeContentPage;
